import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  StarIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import AccountModal from '../components/AccountModal';
import { getModalTheme, focusBorder, type BioModalTheme } from '../components/bioModalTheme';

// Helper to determine if text should be dark or light based on background color
const getContrastTextColor = (hexColor: string): string => {
  // Default to white if no color provided
  if (!hexColor) return '#ffffff';
  
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return dark text for light backgrounds, white text for dark backgrounds
  return luminance > 0.6 ? '#000000' : '#ffffff';
};

interface TalentProfile {
  id: string;
  user_id: string;
  username?: string;
  full_name?: string;
  temp_full_name?: string; // Name stored before onboarding completion
  temp_avatar_url?: string;
  bio?: string;
  social_accounts?: SocialAccount[];
  promo_video_url?: string;
  is_verified?: boolean;
  // Social handles from talent_profiles table
  twitter_handle?: string;
  instagram_handle?: string;
  facebook_handle?: string;
  tiktok_handle?: string;
  rumble_handle?: string;
  rumble_type?: 'c' | 'user'; // 'c' for channel, 'user' for user profile
  rumble_title_filter?: string; // Optional filter to match video titles
  youtube_handle?: string;
  podcast_rss_url?: string;
  podcast_name?: string;
  podcast_platform_links?: Record<string, string>;
  users?: {
    full_name?: string;
  };
  auto_apply_sponsors?: boolean;
  collabs_enabled?: boolean;
}

interface SocialAccount {
  id: string;
  platform: string;
  handle: string;
  follower_count?: number;
}

// Social platform configurations with SVG icons
const SOCIAL_PLATFORMS: Record<string, { icon: React.ReactNode; baseUrl: string; name: string }> = {
  instagram: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
    baseUrl: 'https://instagram.com/', name: 'Instagram' 
  },
  twitter: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    baseUrl: 'https://x.com/', name: 'X' 
  },
  tiktok: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>,
    baseUrl: 'https://tiktok.com/@', name: 'TikTok' 
  },
  youtube: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    baseUrl: 'https://youtube.com/@', name: 'YouTube' 
  },
  facebook: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    baseUrl: 'https://facebook.com/', name: 'Facebook' 
  },
  linkedin: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    baseUrl: 'https://linkedin.com/in/', name: 'LinkedIn' 
  },
  threads: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.88-.73 2.108-1.146 3.456-1.17 1.005-.018 1.96.14 2.836.469-.034-.773-.17-1.418-.413-1.911-.351-.714-.964-1.08-1.874-1.12-1.084.007-1.9.6-2.378 1.235l-1.7-1.154c.78-1.104 2.144-1.8 3.943-1.963l.136-.008c1.678-.067 3.056.36 4.1 1.27.962.838 1.574 2.086 1.82 3.713.266-.057.54-.103.82-.138l.468-.06c1.953-.19 3.936.376 5.14 1.556 1.522 1.49 1.973 3.746 1.239 6.2-.49 1.638-1.51 3.07-2.95 4.14-1.617 1.2-3.654 1.832-6.058 1.88h-.007zm-2.21-8.106c-.78.045-1.394.283-1.776.69-.345.368-.516.838-.483 1.325.033.515.266.96.657 1.254.453.34 1.09.5 1.798.454 1.053-.057 1.864-.508 2.412-1.342.396-.604.637-1.4.716-2.364-.93-.252-1.956-.345-3.324-.017z"/></svg>,
    baseUrl: 'https://threads.net/@', name: 'Threads' 
  },
  snapchat: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>,
    baseUrl: 'https://snapchat.com/add/', name: 'Snapchat' 
  },
  pinterest: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>,
    baseUrl: 'https://pinterest.com/', name: 'Pinterest' 
  },
  spotify: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>,
    baseUrl: 'https://open.spotify.com/user/', name: 'Spotify' 
  },
  twitch: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>,
    baseUrl: 'https://twitch.tv/', name: 'Twitch' 
  },
  discord: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg>,
    baseUrl: 'https://discord.gg/', name: 'Discord' 
  },
  rumble: { 
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/></svg>,
    baseUrl: 'https://rumble.com/c/', name: 'Rumble' 
  },
};

interface BioSettings {
  id?: string;
  talent_id: string;
  instagram_username?: string;
  one_liner?: string;
  theme: string;
  background_color: string;
  accent_color: string;
  font_family: string;
  show_shoutout_card: boolean;
  show_rumble_card: boolean;
  show_youtube_card?: boolean;
  show_podcast_card?: boolean;
  show_newsletter?: boolean;
  affiliate_section_title?: string;
  is_published: boolean;
  background_type: string;
  gradient_start: string;
  gradient_end: string;
  gradient_direction: string;
  button_style: string;
  button_color: string;
  text_color: string;
  card_style: string;
  card_opacity: number;
  profile_image_url?: string;
  display_name?: string;
  stream_card_order?: string[];
}

interface BioLink {
  id: string;
  talent_id: string;
  link_type: 'basic' | 'grid' | 'newsletter' | 'sponsor' | 'video' | 'affiliate' | 'donate';
  title?: string;
  url?: string;
  icon_url?: string;
  image_url?: string;
  video_url?: string;
  company_name?: string;
  discount_amount?: string;
  discount_code?: string;
  grid_size?: 'small' | 'medium' | 'large';
  display_order: number;
  is_active: boolean;
  grid_columns?: number;
  background_image_url?: string;
  thumbnail_url?: string;
  subtitle?: string;
  button_text?: string;
  is_featured?: boolean;
  click_count?: number;
  link_format?: 'thin' | 'tall' | 'square';
  expires_at?: string | null;
}

interface Review {
  id: string;
  rating: number;
  comment?: string;
  created_at: string;
  users?: {
    full_name?: string;
  };
}

interface NewsletterConfig {
  id: string;
  talent_id: string;
  provider: string;
  api_key?: string | null;
  list_id?: string | null;
  webhook_url?: string | null;
  form_id?: string | null;
  is_active: boolean;
}

interface RumbleVideoData {
  title: string;
  thumbnail: string;
  url: string;
  views: number;
  isLive: boolean;
  liveViewers?: number;
}

interface ServiceOffering {
  id: string;
  talent_id: string;
  service_type: 'instagram_collab' | 'tiktok_collab' | 'youtube_collab' | 'sponsorship';
  pricing: number; // in cents
  title: string;
  description?: string;
  video_length_seconds: number;
  benefits: string[];
  platforms: string[]; // Which platforms are included in this collab
  is_active: boolean;
  total_followers?: number;
}

interface BioEvent {
  id: string;
  talent_id: string;
  title: string;
  description?: string;
  event_date?: string;
  event_end_date?: string;
  event_time?: string;
  location?: string;
  registration_url?: string;
  button_text: string;
  image_url?: string;
  source_type: 'manual' | 'ical' | 'rss';
  source_url?: string;
  is_active: boolean;
  display_order: number;
}

interface YouTubeVideoData {
  title: string;
  thumbnail: string;
  url: string;
  views: number;
  isLive: boolean;
  liveViewers?: number;
  channelUrl: string;
}

interface PodcastEpisodeData {
  title: string;
  description?: string;
  thumbnail?: string;
  url: string;
  audioUrl?: string;
  pubDate?: string;
  duration?: string;
  views?: string;
  podcastName: string;
  feedUrl: string;
  listenLinks?: {
    spotify?: string;
    apple?: string;
    youtube?: string;
    google?: string;
  };
}

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// User type for current logged-in user
interface CurrentUser {
  id: string;
  email?: string;
  phone?: string;
  full_name?: string;
  credits?: number;
  avatar_url?: string | null;
}

// Helper function to send subscriber data to newsletter integrations
const sendToNewsletterIntegration = async (
  config: NewsletterConfig,
  data: {
    email: string;
    phone?: string | null;
    name?: string;
    talent_id: string;
    talent_name: string;
  }
) => {
  const { provider, api_key, list_id, webhook_url } = config;

  // Always send to webhook if configured (works for Zapier, custom webhooks, etc.)
  if (webhook_url) {
    await fetch(webhook_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        source: 'shoutout_bio_follow',
        provider,
        timestamp: new Date().toISOString(),
      }),
    });
  }

  // Provider-specific API calls (if API key is configured)
  if (api_key && list_id) {
    switch (provider) {
      case 'mailchimp':
        // Mailchimp uses a datacenter in the API key (e.g., us1, us2)
        const dc = api_key.split('-').pop() || 'us1';
        await fetch(`https://${dc}.api.mailchimp.com/3.0/lists/${list_id}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${btoa(`anystring:${api_key}`)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email_address: data.email,
            status: 'subscribed',
            merge_fields: {
              FNAME: data.name?.split(' ')[0] || '',
              LNAME: data.name?.split(' ').slice(1).join(' ') || '',
              PHONE: data.phone || '',
            },
            tags: ['shoutout_bio', data.talent_name],
          }),
        });
        break;

      case 'getresponse':
        await fetch('https://api.getresponse.com/v3/contacts', {
          method: 'POST',
          headers: {
            'X-Auth-Token': `api-key ${api_key}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: data.email,
            campaign: { campaignId: list_id },
            name: data.name || data.email.split('@')[0],
            customFieldValues: [
              { customFieldId: 'phone', value: [data.phone || ''] },
              { customFieldId: 'source', value: ['shoutout_bio'] },
            ],
          }),
        });
        break;

      case 'activecampaign':
        // ActiveCampaign API URL is usually like: https://youraccountname.api-us1.com
        // The list_id here would be the account URL
        await fetch(`${list_id}/api/3/contacts`, {
          method: 'POST',
          headers: {
            'Api-Token': api_key,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contact: {
              email: data.email,
              firstName: data.name?.split(' ')[0] || '',
              lastName: data.name?.split(' ').slice(1).join(' ') || '',
              phone: data.phone || '',
            },
          }),
        });
        break;

      // For other providers, rely on webhook
      default:
        console.log(`Provider ${provider} uses webhook integration`);
    }
  }
};

const BioPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const showTopFans = true;
  const [talentProfile, setTalentProfile] = useState<TalentProfile | null>(null);
  const [bioSettings, setBioSettings] = useState<BioSettings | null>(null);
  const [links, setLinks] = useState<BioLink[]>([]);
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [randomReview, setRandomReview] = useState<Review | null>(null);
  const [newsletterConfig, setNewsletterConfig] = useState<NewsletterConfig | null>(null);
  const [rumbleData, setRumbleData] = useState<RumbleVideoData | null>(null);
  const [youtubeData, setYoutubeData] = useState<YouTubeVideoData | null>(null);
  const [podcastData, setPodcastData] = useState<PodcastEpisodeData | null>(null);
  const [fetchingPodcast, setFetchingPodcast] = useState(false);
  const podcastFetchedRef = React.useRef(false);
  const [serviceOfferings, setServiceOfferings] = useState<ServiceOffering[]>([]);
  const [showCollabModal, setShowCollabModal] = useState<ServiceOffering | null>(null);
  const [showSponsorModal, setShowSponsorModal] = useState<ServiceOffering | null>(null);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [bioEvents, setBioEvents] = useState<BioEvent[]>([]);
  const [sponsorAds, setSponsorAds] = useState<{ assignment_id: string; campaign_id: string; sponsor_id: string; ad_id?: string; title: string; subtitle?: string; logo_url?: string; link_url: string; discount_text?: string; creator_media_url?: string; creator_media_kind?: 'video' | 'image'; creative_id?: string; is_house_ad?: boolean; featured_avatars?: string[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isMonthlySupporter, setIsMonthlySupporter] = useState(false);
  const [supporterMonths, setSupporterMonths] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  // (button text rotation removed — static copy now)
  // barTextIndex removed — static button text, no rotation
  const [showUpZip, setShowUpZip] = useState(''); // For Show Up Now card (Kaylee Campbell)
  const [stayBarPulse, setStayBarPulse] = useState(false);
  const stayBarRef = useRef<HTMLDivElement | null>(null);
  const [stayBarSize, setStayBarSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAdsEmbed, setIsAdsEmbed] = useState(false);
  const [orderOverlay, setOrderOverlay] = useState<{ videoUrl: string; talentName: string; orderId: string } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [orderOverlayLoading, setOrderOverlayLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Fan leaderboard + check-in
  type LeaderboardRow = { rank: number; user_id: string | null; fan_email: string | null; display_name: string; avatar_url: string | null; points: number };
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [fanRank, setFanRank] = useState<{ rank: number; points: number; total_fans: number; next_checkin_at: string | null } | null>(null);
  const [coinFlipped, setCoinFlipped] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInWavesKey, setCheckInWavesKey] = useState(0);
  const [showAccountModal, setShowAccountModal] = useState(false);

  // Hard render-time guard: if BioPage is loaded inside ANY iframe (cross
  // origin or same origin) OR with a known "embedded" query param, we never
  // auto-trigger the Become-a-Fan popup. Computed once on first render so
  // the result can't drift across re-renders.
  const isEmbeddedContext = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.self !== window.top) return true;
    } catch {
      return true;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('source')) return true;
      if (params.get('popup') === '0') return true;
      if (params.get('embed') === '1') return true;
      if (params.get('embedded') === '1') return true;
    } catch {}
    try {
      const ref = document.referrer || '';
      if (ref.includes('ads.shoutout.us')) return true;
      if (ref.includes('creator.shoutout.us')) return true;
    } catch {}
    return false;
  }, []);
  // When an anonymous fan taps "Check In", we open the login modal and remember
  // the intent here so the check-in auto-completes as soon as auth finishes —
  // no second tap needed. The animation waits until the modal has fully
  // closed so the waves don't fire behind the login sheet.
  const pendingCheckInRef = useRef(false);
  // Track the modal's previous open state so we can detect the "just closed"
  // edge and trigger the deferred check-in animation at that moment.
  const prevShowAccountModalRef = useRef(false);

  // MacBook Neo giveaway card (megainspo + mericainspo + shoutout)
  const NEO_GIVEAWAY_USERNAMES = ['megainspo', 'mericainspo', 'shoutout', 'americainspo', 'magainspo'];
  const NEO_END_DATE = new Date('2026-05-22T23:59:59-05:00');
  const isNeoProfile = NEO_GIVEAWAY_USERNAMES.includes((username || '').toLowerCase());
  const [neoEntered, setNeoEntered] = useState(false);
  const [neoWavesKey, setNeoWavesKey] = useState(0);
  const pendingNeoRef = useRef(false);
  const prevShowAccountModalForNeoRef = useRef(false);

  // "Become {first}'s Fan" deferred follow + post-success waves on the user
  // icon — same pattern as check-in: anonymous tap opens AccountModal, the
  // follow + animation auto-fires after the modal has closed and currentUser
  // is populated.
  const [followWavesKey, setFollowWavesKey] = useState(0);
  const pendingFollowRef = useRef(false);
  const prevShowAccountModalForFollowRef = useRef(false);

  // Become-a-Fan exit-intent / 8-second popup state. The same AccountModal
  // is reused — `accountModalContext` switches the modal's framing into a
  // conversion-focused header + perks checklist when set to 'becomeFan'.
  // We fire-once-per-session and respect a sessionStorage dismissal so we
  // don't badger fans who already said no.
  const [accountModalContext, setAccountModalContext] = useState<'default' | 'becomeFan'>('default');
  const becomeFanShownRef = useRef(false);
  const prevShowAccountModalForBecomeFanRef = useRef(false);

  // Featured talent avatars — loaded for the email-style ShoutOut card on username='shoutout'
  const [featuredTalentAvatars, setFeaturedTalentAvatars] = useState<{ id: string; name: string; avatar: string }[]>([]);

  const downloadVideoFile = useCallback(async (url: string, filename: string) => {
    setDownloading(true);
    try {
      const resp = await fetch(url, { mode: 'cors' });
      if (!resp.ok) throw new Error('Failed to fetch video');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 300);
      toast.success('Download started!');
    } catch {
      // Fallback: use Content-Disposition via anchor tag
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started!');
    } finally {
      setDownloading(false);
    }
  }, []);

  useEffect(() => {
    const orderId = searchParams.get('order');
    if (!orderId || orderOverlay || !talentProfile) return;
    setOrderOverlayLoading(true);
    supabase
      .from('orders')
      .select('id, video_url, recipient_name')
      .eq('id', orderId)
      .not('video_url', 'is', null)
      .maybeSingle()
      .then(({ data: order }) => {
        if (order?.video_url) {
          setOrderOverlay({
            videoUrl: order.video_url,
            talentName: talentProfile.full_name || talentProfile.temp_full_name || 'ShoutOut',
            orderId: order.id,
          });
        }
        setOrderOverlayLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, talentProfile]);


  // (button text rotation removed — static copy now)

  // (bar text rotation removed — static copy now)

  // One-time attention pulse around the Stay Connected bar ~5s after page loads
  useEffect(() => {
    if (loading) return;
    const measure = () => {
      if (stayBarRef.current) {
        setStayBarSize({ w: stayBarRef.current.offsetWidth, h: stayBarRef.current.offsetHeight });
      }
    };
    const startTimer = setTimeout(() => { measure(); setStayBarPulse(true); }, 5000);
    const stopTimer = setTimeout(() => setStayBarPulse(false), 5000 + 2400);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(stopTimer);
    };
  }, [loading]);

  // Fetch unread notifications via edge function (bypasses RLS)
  useEffect(() => {
    if (!currentUser?.id) return;
    const fetchNotifications = async () => {
      try {
        const { data } = await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'get-user-notifications', userId: currentUser.id, email: currentUser.email },
        });
        if (typeof data?.count === 'number') setUnreadNotifications(data.count);
      } catch {}
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [currentUser?.id, currentUser?.email]);

  // Update page title with talent name
  // Update page title and meta tags for social sharing
  useEffect(() => {
    const name = talentProfile?.full_name || talentProfile?.temp_full_name || bioSettings?.display_name || 'Creator';
    const description = bioSettings?.one_liner || `${name}'s link in bio`;
    const userAvatarUrl = (talentProfile as any)?.users?.avatar_url || '';
    const imageUrl = talentProfile?.temp_avatar_url || bioSettings?.profile_image_url || userAvatarUrl;
    const pageUrl = `https://shoutout.fans/${talentProfile?.username || talentProfile?.id || ''}`;
    
    const pageTitle = `${name} | ShoutOut Fans`;
    
    // Set page title
    document.title = pageTitle;
    
    // Helper to update or create meta tag
    const setMetaTag = (property: string, content: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let meta = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    // Open Graph tags (Facebook, iMessage, etc.)
    setMetaTag('og:title', pageTitle);
    setMetaTag('og:description', description);
    setMetaTag('og:site_name', 'ShoutOut Fans');
    setMetaTag('og:type', 'profile');
    setMetaTag('og:url', pageUrl);
    if (imageUrl) {
      setMetaTag('og:image', imageUrl);
    }
    
    // Twitter Card tags
    setMetaTag('twitter:card', 'summary', true);
    setMetaTag('twitter:title', pageTitle, true);
    setMetaTag('twitter:description', description, true);
    if (imageUrl) {
      setMetaTag('twitter:image', imageUrl, true);
    }
    
    // Standard description meta
    setMetaTag('description', description, true);
    
    return () => {
      document.title = 'ShoutOut Fans';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentProfile?.full_name, talentProfile?.temp_full_name, talentProfile?.username, talentProfile?.id, talentProfile?.temp_avatar_url, (talentProfile as any)?.users?.avatar_url, bioSettings?.display_name, bioSettings?.one_liner, bioSettings?.profile_image_url]);

  useEffect(() => {
    const fetchBioData = async () => {
      if (!username) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        // Try to find by username first, then by ID, including user data for fallback name
        let { data: profile, error: profileError } = await supabase
          .from('talent_profiles')
          .select(`
            *,
            users!talent_profiles_user_id_fkey (
              full_name,
              avatar_url
            )
          `)
          .ilike('username', username)
          .single();

        if (profileError || !profile) {
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username);
          if (isUuid) {
            const { data: profileById, error: idError } = await supabase
              .from('talent_profiles')
              .select(`
                *,
                users!talent_profiles_user_id_fkey (
                  full_name,
                  avatar_url
                )
              `)
              .eq('id', username)
              .single();

            if (!idError && profileById) {
              profile = profileById;
            }
          }

          if (!profile) {
            setNotFound(true);
            setLoading(false);
            return;
          }
        }

        setTalentProfile(profile);

        // Detect ads embed context
        const urlParams = new URLSearchParams(window.location.search);
        const isInIframe = window.self !== window.top;
        if (urlParams.get('source') === 'ads' || (isInIframe && document.referrer.includes('ads.shoutout.us'))) {
          setIsAdsEmbed(true);
        }

        // Track page view (skip if preview mode)
        const isPreview = urlParams.get('preview') === 'true';
        
        if (!isPreview) {
          // GA4 custom event for accurate, deduplicated view tracking
          (window as any).gtag?.('event', 'bio_page_view', {
            talent_slug: username,
          });

          // Viewable impression: fire once the tab has been visible for
          // ≥1 continuous second. Bounces (<1s) and background-tab prefetches
          // are excluded from viewport_qualified. Using document visibility
          // (instead of IntersectionObserver with an intersectionRatio
          // threshold) avoids a subtle bug where long pages never reach the
          // ratio threshold and therefore never qualify any view.
          const trackView = async (qualified: boolean) => {
            try {
              await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/track-bio-view`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({
                  talent_id: profile.id,
                  referrer: document.referrer || null,
                  utm_source: urlParams.get('utm_source'),
                  utm_medium: urlParams.get('utm_medium'),
                  utm_campaign: urlParams.get('utm_campaign'),
                  viewport_qualified: qualified,
                }),
              });
            } catch (error) {
              console.error('Failed to track view:', error);
            }
          };

          let qualifyTimer: ReturnType<typeof setTimeout> | null = null;
          let recorded = false;
          const QUALIFY_AFTER_MS = 1000;

          const markQualified = () => {
            if (recorded) return;
            recorded = true;
            if (qualifyTimer) { clearTimeout(qualifyTimer); qualifyTimer = null; }
            document.removeEventListener('visibilitychange', onVisChange);
            window.removeEventListener('pagehide', onPageHide);
            void trackView(true);
          };

          const startQualifyTimer = () => {
            if (recorded || qualifyTimer) return;
            qualifyTimer = setTimeout(markQualified, QUALIFY_AFTER_MS);
          };

          const cancelQualifyTimer = () => {
            if (qualifyTimer) { clearTimeout(qualifyTimer); qualifyTimer = null; }
          };

          const onVisChange = () => {
            if (document.visibilityState === 'visible') startQualifyTimer();
            else cancelQualifyTimer();
          };

          // If the user navigates away before the 1s dwell completes, still
          // record an (unqualified) view so we don't undercount raw traffic.
          const onPageHide = () => {
            if (recorded) return;
            recorded = true;
            cancelQualifyTimer();
            document.removeEventListener('visibilitychange', onVisChange);
            window.removeEventListener('pagehide', onPageHide);
            void trackView(false);
          };

          if (document.visibilityState === 'visible') startQualifyTimer();
          document.addEventListener('visibilitychange', onVisChange);
          window.addEventListener('pagehide', onPageHide, { once: true });
        }

        // Get bio settings - auto-create if they don't exist
        let { data: settings, error: settingsError } = await supabase
          .from('bio_settings')
          .select('*')
          .eq('talent_id', profile.id)
          .single();

        // If no settings exist, create default ones (auto-published)
        if (settingsError && settingsError.code === 'PGRST116') {
          const defaultSettings = {
            talent_id: profile.id,
            theme: 'glass',
            background_color: '#0a0a0a',
            accent_color: '#3b82f6',
            font_family: 'Inter',
            show_shoutout_card: true,
            is_published: true, // Auto-publish by default
            background_type: 'gradient',
            gradient_start: '#0a0a0a',
            gradient_end: '#1a1a2e',
            gradient_direction: 'to-b',
            button_style: 'rounded',
            button_color: '#3b82f6',
            text_color: '#ffffff',
            card_style: 'glass',
            card_opacity: 0.10,
            display_name: profile.full_name,
            profile_image_url: profile.temp_avatar_url,
          };

          const { data: newSettings, error: createError } = await supabase
            .from('bio_settings')
            .insert([defaultSettings])
            .select()
            .single();

          if (!createError && newSettings) {
            settings = newSettings;
          }
        }

        // If no settings could be created, build minimal defaults so the page still renders
        // The bio page should always show if the talent profile exists (active or not)
        if (!settings) {
          settings = {
            talent_id: profile.id,
            theme: 'glass',
            background_color: '#0a0a0a',
            accent_color: '#3b82f6',
            font_family: 'Inter',
            show_shoutout_card: true,
            is_published: true,
            background_type: 'gradient',
            gradient_start: '#0a0a0a',
            gradient_end: '#1a1a2e',
            gradient_direction: 'to-b',
            button_style: 'rounded',
            button_color: '#3b82f6',
            text_color: '#ffffff',
            card_style: 'glass',
            card_opacity: 0.10,
            display_name: profile.full_name || profile.temp_full_name,
            profile_image_url: profile.temp_avatar_url,
          } as any;
        }
        
        // Ensure profile_image_url is populated from temp_avatar_url if missing
        if (!settings.profile_image_url && profile.temp_avatar_url) {
          settings.profile_image_url = profile.temp_avatar_url;
        }

        setBioSettings(settings);

        // Get links (filter out any that have expired)
        const { data: linksData } = await supabase
          .from('bio_links')
          .select('*')
          .eq('talent_id', profile.id)
          .eq('is_active', true)
          .order('display_order');

        const now = Date.now();
        const unexpired = (linksData || []).filter((l: any) => !l.expires_at || new Date(l.expires_at).getTime() > now);
        setLinks(unexpired);

        // Get a random review with user info
        const { data: reviews } = await supabase
          .from('reviews')
          .select('*, users:user_id(full_name)')
          .eq('talent_id', profile.id)
          .gte('rating', 4)
          .limit(10);

        const textReviews = (reviews || []).filter((r: any) => r.comment && String(r.comment).trim().length > 0);
        if (textReviews.length > 0) {
          const randomIndex = Math.floor(Math.random() * textReviews.length);
          setRandomReview(textReviews[randomIndex]);
        } else {
          setRandomReview(null);
        }

        // Get newsletter config
        const { data: newsletterData } = await supabase
          .from('bio_newsletter_configs')
          .select('*')
          .eq('talent_id', profile.id)
          .eq('is_active', true)
          .limit(1);

        if (newsletterData && newsletterData.length > 0) {
          setNewsletterConfig(newsletterData[0]);
        }

        // Get social accounts from the social_accounts table
        const { data: socialData } = await supabase
          .from('social_accounts')
          .select('id, platform, handle, follower_count')
          .eq('talent_id', profile.id);
        
        console.log('Social accounts from DB:', socialData);
        
        // Combine social_accounts with handles from talent_profiles
        // This ensures handles added via admin show up even if not in social_accounts
        const combinedAccounts: SocialAccount[] = [];
        
        // First add all from social_accounts table
        if (socialData && socialData.length > 0) {
          for (const s of socialData) {
            combinedAccounts.push({
              id: s.id,
              platform: s.platform,
              handle: s.handle.replace(/^@/, ''),
              follower_count: s.follower_count,
            });
          }
        }
        
        // Then add any handles from talent_profiles that aren't already in the list
        const handleMappings = [
          { platform: 'twitter', field: 'twitter_handle' },
          { platform: 'instagram', field: 'instagram_handle' },
          { platform: 'facebook', field: 'facebook_handle' },
          { platform: 'tiktok', field: 'tiktok_handle' },
          { platform: 'youtube', field: 'youtube_handle' },
          { platform: 'rumble', field: 'rumble_handle' },
        ];
        
        // Get follower_counts from talent_profiles (fallback storage)
        const profileFollowerCounts = (profile as any).follower_counts || {};
        console.log('Profile follower_counts from DB:', profileFollowerCounts);
        
        for (const mapping of handleMappings) {
          const handle = (profile as any)[mapping.field];
          if (handle) {
            const existsAlready = combinedAccounts.some(s => s.platform === mapping.platform);
            if (!existsAlready) {
              combinedAccounts.push({
                id: `profile-${mapping.platform}`,
                platform: mapping.platform,
                handle: handle.replace(/^@/, ''),
                follower_count: profileFollowerCounts[mapping.platform] || undefined,
              });
            } else {
              // If it exists but doesn't have a follower count, try to get it from profile
              const existingAccount = combinedAccounts.find(s => s.platform === mapping.platform);
              if (existingAccount && !existingAccount.follower_count && profileFollowerCounts[mapping.platform]) {
                existingAccount.follower_count = profileFollowerCounts[mapping.platform];
              }
            }
          }
        }
        
        if (combinedAccounts.length > 0) {
          setSocialAccounts(combinedAccounts);
          fetchFollowerCounts(combinedAccounts, profile.id);
        }

        // Get active service offerings
        const { data: servicesData } = await supabase
          .from('service_offerings')
          .select('*')
          .eq('talent_id', profile.id)
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (servicesData && servicesData.length > 0) {
          const filtered = servicesData.filter(s =>
            s.service_type === 'sponsorship' || profile.collabs_enabled
          );
          setServiceOfferings(filtered.map(s => ({
            ...s,
            benefits: Array.isArray(s.benefits) ? s.benefits : JSON.parse(s.benefits || '[]'),
            platforms: Array.isArray(s.platforms) ? s.platforms : JSON.parse(s.platforms || '["instagram"]'),
          })));
        }

        // Get active bio events
        const { data: eventsData } = await supabase
          .from('bio_events')
          .select('*')
          .eq('talent_id', profile.id)
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (eventsData && eventsData.length > 0) {
          setBioEvents(eventsData);
        }

        // Fetch Rumble data - first try cache, then fall back to live scraping
        if (profile.rumble_handle) {
          fetchRumbleData(profile.id, profile.rumble_handle, profile.rumble_type, profile.full_name || profile.temp_full_name);
        }

        // Fetch YouTube data - same caching strategy
        if (profile.youtube_handle) {
          fetchYouTubeData(profile.id, profile.youtube_handle);
        }

        // Fetch Podcast data if RSS feed is configured
        if (profile.podcast_rss_url) {
          fetchPodcastData(profile.podcast_rss_url, profile.podcast_name || 'Podcast', profile.id, profile.podcast_platform_links);
        }

        // Fetch sponsor ads if enabled globally or via per-talent override
        const { data: adsSetting } = await supabase
          .from('platform_settings')
          .select('setting_value')
          .eq('setting_key', 'sponsor_ads_enabled')
          .single();

        const globalSponsorAdsEnabled = adsSetting?.setting_value === 'true';
        // auto_apply_sponsors on the talent profile is the DB-driven per-talent switch
        const showSponsorAds = globalSponsorAdsEnabled || !!profile.auto_apply_sponsors;

        if (showSponsorAds) {
          // Two-step fetch: embedded joins sometimes return empty for anon clients on public bios;
          // dashboard preview uses an authenticated session and still sees rows.
          const { data: assignData, error: assignErr } = await supabase
            .from('sponsor_creator_assignments')
            .select('id, campaign_id, sponsor_id, traffic_weight')
            .eq('talent_id', profile.id)
            .eq('is_active', true);

          if (assignErr) {
            console.warn('Sponsor assignments fetch failed:', assignErr.message);
          }

          if (assignData && assignData.length > 0) {
            const campaignIds = Array.from(new Set(assignData.map((a) => a.campaign_id)));
            // Two-step rotation:
            //   - pick_campaign_ad picks the copy variant (title/subtitle)
            //   - pick_campaign_creative picks the media variant (video or
            //     static logo) from the campaign's pool
            // Independent CTR scoring lets us optimise copy and media in
            // parallel. The combination shown to a viewer is whichever
            // copy × creative the two RPCs randomly returned.
            //
            // Pull is_house_ad too — the house ad has a separate fetch path
            // below (independent of assignments, with a different render
            // style) so we skip it here to avoid showing the same campaign
            // twice on the bio.
            const { data: campaigns, error: campErr } = await supabase
              .from('sponsor_campaigns')
              .select('id, title, subtitle, logo_url, link_url, discount_text, blocked_talent_ids, animated_logo_url, animated_logo_status, is_house_ad')
              .in('id', campaignIds)
              .eq('is_active', true);

            if (campErr) {
              console.warn('Sponsor campaigns fetch failed:', campErr.message);
            }

            const byId = new Map((campaigns || []).map((c: any) => [c.id, c]));

            const adPromises = assignData.map(async (a: any) => {
              const c = byId.get(a.campaign_id);
              if (!c) return null;
              if (c.is_house_ad) return null; // rendered below via the dedicated house ad path
              if (c.blocked_talent_ids && c.blocked_talent_ids.includes(profile.id)) return null;

              // CTR-weighted traffic distribution: traffic_weight=1.0 → always show,
              // traffic_weight=0.3 → show ~30% of visits. Higher-CTR creators win more traffic.
              const weight = typeof a.traffic_weight === 'number' ? a.traffic_weight : 1.0;
              if (Math.random() > weight) return null;

              // Pick the copy variant first; the creative pick reads
              // the chosen ad's CTR to decide whether to force the
              // top-CTR creative (probation: 1-2% CTR ad → best
              // creative) or rotate randomly. Sequential is fine
              // here — one extra round-trip per campaign per visit
              // is well below the noise floor of the bio page.
              const pickedAd = await supabase.rpc('pick_campaign_ad', { p_campaign_id: a.campaign_id });
              const adRow    = pickedAd?.data?.[0];
              const pickedCreative = await supabase.rpc('pick_campaign_creative', {
                p_campaign_id: a.campaign_id,
                p_ad_id:       adRow?.id ?? null,
              });
              const cvRow = pickedCreative?.data?.[0];

              // Resolve media: prefer the creative pool. Falls back
              // to the legacy single animated_logo_* fields, then to
              // the static logo on the campaign.
              let creator_media_url: string | undefined;
              let creator_media_kind: 'video' | 'image' | undefined;
              let creative_id: string | undefined;

              if (cvRow && cvRow.media_url) {
                creator_media_url  = cvRow.media_url;
                creator_media_kind = (cvRow.kind === 'video' ? 'video' : 'image');
                creative_id        = cvRow.creative_id;
              } else if (c.animated_logo_status === 'ready' && c.animated_logo_url) {
                creator_media_url  = c.animated_logo_url;
                creator_media_kind = /\.(mp4|webm|mov)(?:$|\?)/i.test(c.animated_logo_url) ? 'video' : 'image';
              }

              // No active ad in this campaign — don't show anything
              if (!adRow) return null;

              supabase.rpc('record_ad_impression', {
                p_ad_id:       adRow.id,
                p_campaign_id: a.campaign_id,
                p_talent_id:   profile.id,
              }).then(() => {});
              return {
                assignment_id: a.id,
                campaign_id:   a.campaign_id,
                sponsor_id:    a.sponsor_id,
                ad_id:         adRow.id,
                title:         adRow.title,
                subtitle:      adRow.subtitle,
                logo_url:      adRow.logo_url      || c.logo_url,
                link_url:      adRow.link_url      || c.link_url,
                discount_text: adRow.discount_text || c.discount_text,
                creator_media_url,
                creator_media_kind,
                creative_id,
              };
            });

            const resolvedAds = (await Promise.all(adPromises)).filter(Boolean) as any[];
            if (resolvedAds.length > 0) {
              // Shuffle so different visits see different campaigns up top.
              for (let i = resolvedAds.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [resolvedAds[i], resolvedAds[j]] = [resolvedAds[j], resolvedAds[i]];
              }
              // Dedup by sponsor_id: never show two ads from the same
              // advertiser on the same page. Keeps from looking like a
              // billboard for one brand and improves perceived variety.
              const seenSponsors = new Set<string>();
              const dedup: typeof resolvedAds = [];
              for (const ad of resolvedAds) {
                if (ad.sponsor_id && seenSponsors.has(ad.sponsor_id)) continue;
                if (ad.sponsor_id) seenSponsors.add(ad.sponsor_id);
                dedup.push(ad);
                if (dedup.length >= 2) break; // 2 slots: above + after ShoutOut
              }
              setSponsorAds(dedup);
            }
          }

          // House ad fetch — independent of the assignments table.
          //
          // The "Sponsors" house campaign (sponsor_campaigns.is_house_ad=true)
          // promotes ShoutOut Sponsors itself and shows on every creator
          // with auto_apply_sponsors=true, even creators whose paid
          // assignments were culled by the optimizer for low CTR. That
          // means we can't gate it on sponsor_creator_assignments like the
          // paid path above does.
          //
          // We fetch:
          //   1. pick_house_ad() — DB-side helper that picks a copy variant
          //      from the house campaign's ad pool (same Efraimidis-Spirakis
          //      weighted-random sampler as pick_campaign_ad).
          //   2. featured_creator_avatars view — small list of avatars used
          //      by the 3-overlapping-circles layout. We pull more than we
          //      need so we can randomize per visit.
          try {
            const [houseRes, featuredRes] = await Promise.all([
              supabase.rpc('pick_house_ad', { p_talent_id: profile.id }),
              supabase
                .from('featured_creator_avatars')
                .select('avatar_url, featured_order')
                .order('featured_order', { ascending: true, nullsFirst: false })
                .limit(20),
            ]);

            const houseRow = houseRes?.data?.[0];
            if (houseRow) {
              const featured = (featuredRes?.data || [])
                .map((r: any) => r.avatar_url)
                .filter((u: any): u is string => typeof u === 'string' && u.length > 0);
              // Shuffle, then pick 3. Keeps the circles "rotating" per visit
              // (different 3 of the featured set every page load).
              for (let i = featured.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [featured[i], featured[j]] = [featured[j], featured[i]];
              }
              const featuredAvatars = featured.slice(0, 3);

              // Record an impression so house ad CTR is still observable in
              // the admin dashboard — we just don't act on it (optimizer
              // skips is_house_ad campaigns, finalize-ad-tests skips them too).
              supabase.rpc('record_ad_impression', {
                p_ad_id:       houseRow.ad_id,
                p_campaign_id: houseRow.campaign_id,
                p_talent_id:   profile.id,
              }).then(() => {});

              const houseAd = {
                assignment_id: 'house', // synthetic — house ads bypass assignments
                campaign_id:   houseRow.campaign_id,
                sponsor_id:    houseRow.sponsor_id,
                ad_id:         houseRow.ad_id,
                title:         houseRow.title,
                subtitle:      houseRow.subtitle,
                logo_url:      houseRow.logo_url || undefined,
                link_url:      houseRow.link_url,
                discount_text: houseRow.discount_text || undefined,
                is_house_ad:   true,
                featured_avatars: featuredAvatars,
              } as const;

              // House ad always occupies the top sponsor slot. If we already
              // have two paid ads, displace the second one (still better to
              // show the house ad than a duplicate paid impression).
              setSponsorAds((prev) => {
                const next = [houseAd as any, ...prev.filter((a) => !a.is_house_ad)];
                return next.slice(0, 2);
              });
            }
          } catch (e) {
            console.warn('House ad fetch failed:', (e as Error)?.message || e);
          }
        }

      } catch (error) {
        console.error('Error fetching bio data:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBioData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // Check if user is logged in, previously subscribed, or already following
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        // Check Supabase auth session first
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: userData } = await supabase
            .from('users')
            .select('id, email, phone, full_name, credits, avatar_url')
            .eq('id', session.user.id)
            .single();
          
          if (userData) {
            setCurrentUser(userData);
            if (talentProfile?.id) {
              const { data: followData } = await supabase
                .from('talent_followers')
                .select('id')
                .eq('user_id', userData.id)
                .eq('talent_id', talentProfile.id)
                .single();
              setIsFollowing(!!followData);
              // Check monthly supporter status via edge function (bypasses RLS)
              try {
                const { data: ordersData } = await supabase.functions.invoke('lunarpay-api', {
                  body: { action: 'get-orders', userId: userData.id },
                });
                const monthlyOrder = ordersData?.orders?.find((o: any) => o.occasion === 'monthly_support' && o.talent_id === talentProfile.id);
                if (monthlyOrder) {
                  setIsMonthlySupporter(true);
                  const months = Math.max(1, Math.ceil((Date.now() - new Date(monthlyOrder.created_at).getTime()) / (30 * 24 * 60 * 60 * 1000)));
                  setSupporterMonths(months);
                }
              } catch {}
            }
            return;
          }
        }

        // Check localStorage for previous bio page subscription
        const savedEmail = localStorage.getItem('bio_subscriber_email');
        if (savedEmail && talentProfile?.id) {
          // Look up the user and check if they're following
          const { data: savedUser } = await supabase
            .from('users')
            .select('id, email, phone, full_name, credits, avatar_url')
            .eq('email', savedEmail)
            .single();
          
          if (savedUser) {
            setCurrentUser(savedUser);
            const { data: followData } = await supabase
              .from('talent_followers')
              .select('id')
              .eq('user_id', savedUser.id)
              .eq('talent_id', talentProfile.id)
              .single();
            setIsFollowing(!!followData);
            // Check monthly supporter status via edge function (bypasses RLS)
            try {
              const { data: ordersData } = await supabase.functions.invoke('lunarpay-api', {
                body: { action: 'get-orders', email: savedEmail, userId: savedUser.id },
              });
              const monthlyOrder = ordersData?.orders?.find((o: any) => o.occasion === 'monthly_support' && o.talent_id === talentProfile.id);
              if (monthlyOrder) {
                setIsMonthlySupporter(true);
                const months = Math.max(1, Math.ceil((Date.now() - new Date(monthlyOrder.created_at).getTime()) / (30 * 24 * 60 * 60 * 1000)));
                setSupporterMonths(months);
              }
            } catch {}
          }
        }
      } catch (error) {
        console.log('No active session or error checking:', error);
      }
    };

    if (talentProfile?.id) {
      checkUserSession();
    }
  }, [talentProfile?.id]);

  // Load leaderboard for top-right "Top Fans" panel (top 15, viewport shows 4)
  const reloadLeaderboard = useCallback(async () => {
    if (!talentProfile?.id) return;
    try {
      const { data } = await supabase.rpc('get_talent_leaderboard', {
        p_talent_id: talentProfile.id,
        p_limit: 15,
      });
      if (Array.isArray(data)) {
        // Safety-net on the client too: never show the creator as their
        // own "Top Fan" even if historic rows slipped through earlier.
        const ownerUserId = talentProfile.user_id;
        const ownerNames = new Set(
          [
            talentProfile.username,
            talentProfile.full_name,
            talentProfile.temp_full_name,
            talentProfile.users?.full_name,
          ]
            .map((s) => (s || '').trim().toLowerCase())
            .filter(Boolean)
        );
        setLeaderboard((data as LeaderboardRow[]).filter((row) => {
          if (row.user_id === ownerUserId) return false;
          const display = (row.display_name || '').trim().toLowerCase();
          if (display && ownerNames.has(display)) return false;
          return true;
        }));
      }
    } catch {}
  }, [talentProfile?.id, talentProfile?.user_id, talentProfile?.username, talentProfile?.full_name, talentProfile?.temp_full_name, talentProfile?.users?.full_name]);

  const reloadFanRank = useCallback(async () => {
    if (!talentProfile?.id) return;
    const emailFallback = currentUser?.email || localStorage.getItem('bio_subscriber_email') || null;
    try {
      const { data } = await supabase.rpc('get_fan_rank', {
        p_talent_id: talentProfile.id,
        p_user_id: currentUser?.id || null,
        p_email: emailFallback,
      });
      const row = Array.isArray(data) ? data[0] : data;
      if (row) {
        setFanRank({
          rank: Number(row.rank || 0),
          points: Number(row.points || 0),
          total_fans: Number(row.total_fans || 0),
          next_checkin_at: row.next_checkin_at || null,
        });
      }
    } catch {}
  }, [talentProfile?.id, currentUser?.id, currentUser?.email]);

  useEffect(() => { if (showTopFans) reloadLeaderboard(); }, [reloadLeaderboard, showTopFans]);
  useEffect(() => { if (showTopFans) reloadFanRank(); }, [reloadFanRank, showTopFans]);

  // Check if current user already entered the Neo giveaway
  useEffect(() => {
    if (!currentUser?.id || !isNeoProfile) return;
    supabase
      .from('users')
      .select('user_tags')
      .eq('id', currentUser.id)
      .single()
      .then(({ data }) => {
        if (data?.user_tags?.includes('giveaway_neo')) setNeoEntered(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isNeoProfile]);

  // Fetch featured talent avatars for email-style ShoutOut card (username='shoutout' only)
  useEffect(() => {
    if (username?.toLowerCase() !== 'shoutout') return;
    supabase
      .from('talent_profiles')
      .select('id, temp_full_name, temp_avatar_url, featured_order')
      .eq('is_active', true)
      .eq('is_featured', true)
      .not('temp_avatar_url', 'is', null)
      .order('featured_order', { ascending: true, nullsFirst: false })
      .limit(8)
      .then(({ data, error }) => {
        if (error) {
          console.error('featured talent fetch failed:', error);
          return;
        }
        if (data) {
          setFeaturedTalentAvatars(
            data
              .filter((t: any) => t.temp_avatar_url)
              .map((t: any) => ({ id: t.id, name: t.temp_full_name || '', avatar: t.temp_avatar_url }))
          );
        }
      });
  }, [username]);

  // 1-second tick so countdowns stay live without re-fetching
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // Flip cycle: the gap before each flip-to-back grows so the coin doesn't
  // feel twitchy. Front dwell starts at 4s and increases by 2s each cycle
  // (4s → 6s → 8s → ...). Back dwell stays at 4s. Paused while on cooldown.
  useEffect(() => {
    if (!showTopFans) return;
    if (!talentProfile?.id) return;
    if (fanRank?.next_checkin_at && new Date(fanRank.next_checkin_at).getTime() > Date.now()) return;

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    let frontDwell = 4000;
    const backDwell = 4000;

    const cycle = () => {
      if (cancelled) return;
      timeout = setTimeout(() => {
        if (cancelled) return;
        setCoinFlipped(true);
        timeout = setTimeout(() => {
          if (cancelled) return;
          setCoinFlipped(false);
          frontDwell += 2000;
          cycle();
        }, backDwell);
      }, frontDwell);
    };
    cycle();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [talentProfile?.id, fanRank?.next_checkin_at]);

  // Core post-auth check-in: hits the RPC, fires waves/animation, updates rank.
  // Kept separate from `handleCheckIn` so it can be auto-invoked after login
  // without re-tracking the click or re-checking the modal gate.
  const runCheckInCore = useCallback(async () => {
    if (checkingIn) return;
    if (!talentProfile?.id) return;
    setCheckingIn(true);
    // If the tap came from the front face, flip to the back so the fan sees
    // the countdown pill/waves land where the "Check In" affordance was.
    setCoinFlipped(true);
    // Optimistically flip the button into a countdown immediately so the UI
    // feels snappy; the server's real next_checkin_at replaces this below.
    const optimisticNext = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    const prevRank = fanRank;
    setFanRank((prev) => prev
      ? { ...prev, next_checkin_at: optimisticNext }
      : { rank: 0, points: 0, total_fans: 0, next_checkin_at: optimisticNext });
    try {
      const { data, error } = await supabase.rpc('record_fan_checkin', { p_talent_id: talentProfile.id });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.ok) {
        setCheckInWavesKey((k) => k + 1);
        toast.success(`+1 point · ${row.total_points} total`);
        if (row.next_checkin_at) {
          setFanRank((prev) => prev ? { ...prev, points: Number(row.total_points || prev.points), next_checkin_at: row.next_checkin_at } : prev);
        }
        reloadLeaderboard();
        reloadFanRank();
        // Linger on the back face for a moment so the fan sees the countdown
        // take over where the "Check In" button was, then flip back to the photo.
        setTimeout(() => setCoinFlipped(false), 3500);
      } else {
        // Silent no-op — the back face already shows the live countdown so
        // the fan can see why it didn't count. Leave it flipped briefly.
        setFanRank((prev) => prev ? { ...prev, next_checkin_at: row?.next_checkin_at || prev.next_checkin_at } : prev);
        setTimeout(() => setCoinFlipped(false), 3500);
      }
    } catch (err: any) {
      // Network / unexpected error → revert optimistic cooldown
      setFanRank(prevRank);
      toast.error(err?.message || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  }, [checkingIn, talentProfile?.id, fanRank, reloadLeaderboard, reloadFanRank]);

  const handleCheckIn = useCallback(async () => {
    if (checkingIn) return;
    if (!talentProfile?.id) return;
    trackCardClick('check_in');
    if (!currentUser?.id) {
      // Remember the intent so auth completion auto-fires the check-in after
      // the login modal has fully closed (see the effect below).
      pendingCheckInRef.current = true;
      setShowAccountModal(true);
      return;
    }
    await runCheckInCore();
  }, [checkingIn, talentProfile?.id, currentUser?.id, runCheckInCore]);

  // Fire the deferred check-in animation/RPC only after the login modal has
  // closed. This keeps the waves + coin flip from playing behind the sheet
  // during its ~1.4s success state. If the fan cancelled (no currentUser),
  // we just drop the pending intent.
  useEffect(() => {
    const wasOpen = prevShowAccountModalRef.current;
    prevShowAccountModalRef.current = showAccountModal;
    if (wasOpen && !showAccountModal) {
      if (pendingCheckInRef.current && currentUser?.id) {
        pendingCheckInRef.current = false;
        runCheckInCore();
      } else {
        pendingCheckInRef.current = false;
      }
    }
  }, [showAccountModal, currentUser?.id, runCheckInCore]);

  // ── MacBook Neo giveaway entry ──────────────────────────────────────────────
  const runNeoEntryCore = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      // Read current tags then append giveaway_neo if not already present
      const { data: existingUser } = await supabase
        .from('users')
        .select('user_tags, promo_source')
        .eq('id', currentUser.id)
        .single();
      const existingTags: string[] = existingUser?.user_tags || [];
      if (!existingTags.includes('giveaway_neo')) {
        await supabase
          .from('users')
          .update({
            user_tags: [...existingTags, 'giveaway_neo'],
            ...(existingUser?.promo_source ? {} : { promo_source: 'giveaway_neo' }),
          })
          .eq('id', currentUser.id);
      }
      setNeoEntered(true);
      setNeoWavesKey((k) => k + 1);
      toast.success("You're entered! 🎉");
    } catch (err: any) {
      toast.error(err?.message || 'Entry failed, please try again');
    }
  }, [currentUser?.id]);

  const handleNeoEntry = useCallback(() => {
    trackCardClick('neo_giveaway');
    if (!currentUser?.id) {
      pendingNeoRef.current = true;
      setShowAccountModal(true);
      return;
    }
    runNeoEntryCore();
  }, [currentUser?.id, runNeoEntryCore]);

  // Fire neo entry after modal closes (same deferred pattern as check-in)
  useEffect(() => {
    const wasOpen = prevShowAccountModalForNeoRef.current;
    prevShowAccountModalForNeoRef.current = showAccountModal;
    if (wasOpen && !showAccountModal && pendingNeoRef.current && currentUser?.id) {
      pendingNeoRef.current = false;
      runNeoEntryCore();
    } else if (wasOpen && !showAccountModal) {
      pendingNeoRef.current = false;
    }
  }, [showAccountModal, currentUser?.id, runNeoEntryCore]);

  // Fetch follower counts for social accounts
  // Note: Most social platforms block direct scraping. Follower counts can be:
  // 1. Manually entered in the social_accounts table
  // 2. Fetched via official APIs (requires API keys/auth)
  // 3. Updated via a backend service with proper authentication
  const fetchFollowerCounts = async (accounts: SocialAccount[], _talentId: string) => {
    // For now, we just use whatever is in the database
    // The follower_count field should be populated either:
    // - Manually by admin in Supabase
    // - Via a backend cron job with proper API access
    // - Via the Bio Dashboard settings
    
    // Log which accounts don't have follower counts for debugging
    const missingCounts = accounts.filter(a => !a.follower_count || a.follower_count === 0);
    if (missingCounts.length > 0) {
      console.log('Accounts missing follower counts:', missingCounts.map(a => `${a.platform}:@${a.handle}`));
    }
  };

  // Fetch Rumble channel data - show cache immediately, one visitor per 15 min triggers refresh
  const fetchRumbleData = async (talentId: string, rumbleHandle: string, rumbleType?: 'c' | 'user', talentName?: string) => {
    // Clean handle: remove @ prefix and any query parameters (like ?e9s=...)
    const cleanHandle = rumbleHandle.replace(/^@/, '').split('?')[0];
    const urlPrefix = rumbleType || 'c'; // 'c' for channel (default), 'user' for user profile
    const defaultChannelUrl = `https://rumble.com/${urlPrefix}/${cleanHandle}`;
    
    try {
      // Get cached data from rumble_cache table
      const { data: cachedData, error: cacheError } = await supabase
        .from('rumble_cache')
        .select('*')
        .eq('talent_id', talentId)
        .single();
      
      if (!cacheError && cachedData) {
        // Show cached data immediately
        setRumbleData({
          title: decodeHtmlEntities(cachedData.latest_video_title || 'Watch on Rumble'),
          thumbnail: cachedData.latest_video_thumbnail || '',
          url: cachedData.latest_video_url || cachedData.channel_url || defaultChannelUrl,
          views: cachedData.latest_video_views || 0,
          isLive: cachedData.is_live || false,
          liveViewers: cachedData.live_viewers || 0,
        });
        
        // Check if cache is stale (>15 min old) — if so, refresh in background via proxy
        const lastChecked = cachedData.last_checked_at ? new Date(cachedData.last_checked_at).getTime() : 0;
        const fifteenMinAgo = Date.now() - 15 * 60 * 1000;
        if (lastChecked < fifteenMinAgo) {
          refreshRumbleViaProxy(talentId, rumbleHandle, defaultChannelUrl, talentName).catch(e =>
            console.log('Background Rumble refresh failed:', e)
          );
        }
        return;
      }
      
      // No cache - show fallback and try to seed cache via oembed
      setRumbleData({
        title: 'Watch on Rumble',
        thumbnail: '',
        url: defaultChannelUrl,
        views: 0,
        isLive: false,
        liveViewers: 0,
      });
      
      // One-time seed: fetch channel page for first video URL, then oembed for thumbnail
      try {
        const channelResp = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(defaultChannelUrl)}`);
        if (channelResp.ok) {
          const html = await channelResp.text();
          const urlMatch = html.match(/href="(\/v[a-z0-9]+-[^"?]+\.html)/);
          if (urlMatch) {
            const videoUrl = `https://rumble.com${urlMatch[1]}`;
            const oembedResp = await fetch(`https://rumble.com/api/Media/oembed.json?url=${encodeURIComponent(videoUrl)}`);
            if (oembedResp.ok) {
              const oembed = await oembedResp.json();
              if (oembed.thumbnail_url && oembed.title) {
                setRumbleData({
                  title: decodeHtmlEntities(oembed.title),
                  thumbnail: oembed.thumbnail_url,
                  url: videoUrl,
                  views: 0,
                  isLive: false,
                  liveViewers: 0,
                });
                await supabase.functions.invoke('rumble-proxy', {
                  body: {
                    action: 'update-cache',
                    talentId,
                    data: {
                      rumble_handle: rumbleHandle,
                      is_live: false,
                      live_viewers: 0,
                      latest_video_title: decodeHtmlEntities(oembed.title),
                      latest_video_thumbnail: oembed.thumbnail_url,
                      latest_video_url: videoUrl,
                      latest_video_views: 0,
                      channel_url: defaultChannelUrl,
                    },
                  },
                });
              }
            }
          }
        }
      } catch (e) {
        console.log('Rumble seed fetch failed:', e);
      }
      
    } catch (error) {
      console.error('Error fetching Rumble data:', error);
      setRumbleData({
        title: 'Watch on Rumble',
        thumbnail: '',
        url: defaultChannelUrl,
        views: 0,
        isLive: false,
        liveViewers: 0,
      });
    }
  };
  
  // Background refresh: Rumble data via oembed API (reliable, not blocked by Cloudflare)
  const refreshRumbleViaProxy = async (
    talentId: string,
    rumbleHandle: string,
    channelUrl: string,
    _talentName?: string
  ) => {
    try {
      const { data: cachedData } = await supabase
        .from('rumble_cache')
        .select('latest_video_url')
        .eq('talent_id', talentId)
        .single();

      const cachedVideoUrl = cachedData?.latest_video_url;
      if (!cachedVideoUrl || !cachedVideoUrl.includes('rumble.com/v')) return;

      // Refresh via oembed - always works, returns fresh title + thumbnail
      const oembedResp = await supabase.functions.invoke('rumble-proxy', {
        body: { videoUrl: cachedVideoUrl },
      });
      const oembed = oembedResp.data?.oembed;
      if (!oembed?.thumbnail_url || !oembed?.title) return;

      setRumbleData({
        title: decodeHtmlEntities(oembed.title),
        thumbnail: oembed.thumbnail_url,
        url: cachedVideoUrl,
        views: 0,
        isLive: false,
        liveViewers: 0,
      });

      await supabase.functions.invoke('rumble-proxy', {
        body: {
          action: 'update-cache',
          talentId,
          data: {
            rumble_handle: rumbleHandle,
            latest_video_title: decodeHtmlEntities(oembed.title),
            latest_video_thumbnail: oembed.thumbnail_url,
            latest_video_url: cachedVideoUrl,
            channel_url: oembed.author_url || channelUrl,
          },
        },
      });
    } catch (error) {
      console.log('refreshRumbleViaProxy error:', error);
    }
  };

  // YouTube API Key - stored in environment
  const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || '';

  // Fetch YouTube channel data - show cache immediately, one visitor per 15 min triggers refresh
  const fetchYouTubeData = async (talentId: string, youtubeHandle: string) => {
    const cleanHandle = youtubeHandle.trim().replace(/^@/, '');
    const defaultChannelUrl = `https://youtube.com/@${cleanHandle}`;
    
    console.log('fetchYouTubeData called:', { talentId, youtubeHandle, cleanHandle, hasApiKey: !!YOUTUBE_API_KEY, keyPreview: YOUTUBE_API_KEY ? YOUTUBE_API_KEY.substring(0, 8) + '...' : 'none' });
    
    try {
      // Get cached data from youtube_cache table
      const { data: cachedData, error: cacheError } = await supabase
        .from('youtube_cache')
        .select('*')
        .eq('talent_id', talentId)
        .single();
      
      if (!cacheError && cachedData) {
        // Show cached data immediately
        setYoutubeData({
          title: decodeHtmlEntities(cachedData.latest_video_title || 'Watch on YouTube'),
          thumbnail: cachedData.latest_video_thumbnail || '',
          url: cachedData.latest_video_url || defaultChannelUrl,
          views: cachedData.latest_video_views || 0,
          isLive: cachedData.is_live || false,
          liveViewers: cachedData.live_viewers || 0,
          channelUrl: cachedData.channel_url || defaultChannelUrl,
        });

        // Trigger a browser-side refresh when:
        //  • the talent changed their youtube_handle (cached row points to wrong channel), or
        //  • the cache is older than 15 minutes.
        // The server-side cron can silently fail when the API key's HTTP-referrer
        // restriction blocks the edge function's empty Referer, so the visitor's
        // browser (which sends the allowed Referer) keeps the cache honest.
        const cachedHandle = (cachedData.youtube_handle || '').trim().replace(/^@/, '').toLowerCase();
        const handleChanged = cachedHandle !== cleanHandle.toLowerCase();
        const lastChecked = cachedData.last_checked_at ? new Date(cachedData.last_checked_at).getTime() : 0;
        const cacheAgeMs = Date.now() - lastChecked;
        const stale = cacheAgeMs > 15 * 60 * 1000;
        if ((handleChanged || stale) && YOUTUBE_API_KEY) {
          fetchYouTubeInBackground(talentId, youtubeHandle, cleanHandle, defaultChannelUrl).catch(e =>
            console.log('Background YouTube refresh failed:', e)
          );
        }
        return;
      }
      
      // No cache — show fallback, then try client-side API or seed via edge function
      setYoutubeData({
        title: 'Watch on YouTube',
        thumbnail: '',
        url: defaultChannelUrl,
        views: 0,
        isLive: false,
        liveViewers: 0,
        channelUrl: defaultChannelUrl,
      });
      
      if (YOUTUBE_API_KEY) {
        fetchYouTubeInBackground(talentId, youtubeHandle, cleanHandle, defaultChannelUrl);
      } else {
        // No client-side key — invoke edge function to seed this talent's cache
        try {
          console.log('No YouTube API key on client, edge function will handle it');
        } catch (e) {
          console.log('YouTube seed skipped');
        }
      }
      
    } catch (error) {
      console.error('Error fetching YouTube data:', error);
      setYoutubeData({
        title: 'Watch on YouTube',
        thumbnail: '',
        url: defaultChannelUrl,
        views: 0,
        isLive: false,
        liveViewers: 0,
        channelUrl: defaultChannelUrl,
      });
    }
  };
  
  // Background YouTube API fetch - updates cache on SUCCESS only
  const fetchYouTubeInBackground = async (talentId: string, youtubeHandle: string, cleanHandle: string, defaultChannelUrl: string) => {
    try {
      if (!YOUTUBE_API_KEY) {
        console.log('YouTube API key not available in background fetch');
        return;
      }
      
      console.log('Fetching YouTube data for:', cleanHandle);
      
      // First, get the channel ID from the handle
      let channelId = '';
      
      // Try to get channel by handle (newer @handle format)
      const handleResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=id,snippet&forHandle=${cleanHandle}&key=${YOUTUBE_API_KEY}`
      );
      const handleData = await handleResponse.json();
      
      // Log any API errors
      if (handleData.error) {
        console.error('YouTube API error:', handleData.error);
        return;
      }
      
      if (handleData.items && handleData.items.length > 0) {
        channelId = handleData.items[0].id;
      } else {
        // Try as username (older format)
        const usernameResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=id,snippet&forUsername=${cleanHandle}&key=${YOUTUBE_API_KEY}`
        );
        const usernameData = await usernameResponse.json();
        
        if (usernameData.items && usernameData.items.length > 0) {
          channelId = usernameData.items[0].id;
        } else {
          // Maybe it's already a channel ID
          const idResponse = await fetch(
            `https://www.googleapis.com/youtube/v3/channels?part=id,snippet&id=${cleanHandle}&key=${YOUTUBE_API_KEY}`
          );
          const idData = await idResponse.json();
          
          if (idData.items && idData.items.length > 0) {
            channelId = idData.items[0].id;
          }
        }
      }
      
      if (!channelId) {
        console.log('Could not find YouTube channel for:', cleanHandle);
        return;
      }
      
      // Check for live streams first
      const liveResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&eventType=live&type=video&key=${YOUTUBE_API_KEY}`
      );
      const liveData = await liveResponse.json();
      
      let isLive = false;
      let liveViewers = 0;
      let videoId = '';
      let videoTitle = 'Latest Video';
      let videoThumbnail = '';
      
      if (liveData.items && liveData.items.length > 0) {
        // Channel is live!
        isLive = true;
        const liveVideo = liveData.items[0];
        videoId = liveVideo.id.videoId;
        videoTitle = decodeHtmlEntities(liveVideo.snippet.title);
        videoThumbnail = liveVideo.snippet.thumbnails?.high?.url || liveVideo.snippet.thumbnails?.medium?.url || '';
        
        // Get live viewer count
        const statsResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=liveStreamingDetails&id=${videoId}&key=${YOUTUBE_API_KEY}`
        );
        const statsData = await statsResponse.json();
        
        if (statsData.items && statsData.items.length > 0 && statsData.items[0].liveStreamingDetails) {
          liveViewers = parseInt(statsData.items[0].liveStreamingDetails.concurrentViewers || '0');
        }
      } else {
        // Not live - get latest videos (fetch multiple to filter out Shorts)
        const videosResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&order=date&type=video&maxResults=10&key=${YOUTUBE_API_KEY}`
        );
        const videosData = await videosResponse.json();
        
        if (videosData.items && videosData.items.length > 0) {
          // Get video IDs to check their duration (Shorts are ≤60 seconds)
          const videoIds = videosData.items.map((v: any) => v.id.videoId).join(',');
          
          // Fetch video details to get duration
          const detailsResponse = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${videoIds}&key=${YOUTUBE_API_KEY}`
          );
          const detailsData = await detailsResponse.json();
          
          // Parse ISO 8601 duration to seconds
          const parseDuration = (duration: string): number => {
            const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
            if (!match) return 0;
            const hours = parseInt(match[1] || '0');
            const minutes = parseInt(match[2] || '0');
            const seconds = parseInt(match[3] || '0');
            return hours * 3600 + minutes * 60 + seconds;
          };
          
          // Find the first video that's longer than 60 seconds (not a Short)
          let foundRegularVideo = false;
          for (let i = 0; i < videosData.items.length; i++) {
            const video = videosData.items[i];
            const details = detailsData.items?.find((d: any) => d.id === video.id.videoId);
            const duration = details?.contentDetails?.duration || 'PT0S';
            const durationSeconds = parseDuration(duration);
            
            // Skip Shorts and short clips (≤180 seconds / 3 min) - regular videos are typically longer
            if (durationSeconds > 180) {
              videoId = video.id.videoId;
              videoTitle = decodeHtmlEntities(video.snippet.title);
              videoThumbnail = video.snippet.thumbnails?.high?.url || video.snippet.thumbnails?.medium?.url || '';
              foundRegularVideo = true;
              console.log(`Found regular video (${durationSeconds}s): ${videoTitle}`);
              break;
            } else {
              console.log(`Skipping Short (${durationSeconds}s): ${video.snippet.title}`);
            }
          }
          
          // Fallback to first video if no regular videos found
          if (!foundRegularVideo && videosData.items.length > 0) {
            const latestVideo = videosData.items[0];
            videoId = latestVideo.id.videoId;
            videoTitle = decodeHtmlEntities(latestVideo.snippet.title);
            videoThumbnail = latestVideo.snippet.thumbnails?.high?.url || latestVideo.snippet.thumbnails?.medium?.url || '';
            console.log('No regular videos found, using first video:', videoTitle);
          }
        }
      }
      
      if (!videoId) return;
      
      // Get view count for the video
      let views = 0;
      const videoStatsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=statistics&id=${videoId}&key=${YOUTUBE_API_KEY}`
      );
      const videoStatsData = await videoStatsResponse.json();
      
      if (videoStatsData.items && videoStatsData.items.length > 0) {
        views = parseInt(videoStatsData.items[0].statistics?.viewCount || '0');
      }
      
      const videoUrl = `https://youtube.com/watch?v=${videoId}`;
      const channelUrl = `https://youtube.com/channel/${channelId}`;
      
      // Save to cache
      const { data: updateResult } = await supabase
        .from('youtube_cache')
        .upsert({
          talent_id: talentId,
          youtube_handle: youtubeHandle,
          channel_id: channelId,
          is_live: isLive,
          live_viewers: liveViewers,
          latest_video_id: videoId,
          latest_video_title: videoTitle,
          latest_video_thumbnail: videoThumbnail,
          latest_video_url: videoUrl,
          latest_video_views: views,
          channel_url: channelUrl,
          last_checked_at: new Date().toISOString(),
        }, { onConflict: 'talent_id' })
        .select('talent_id');
      
      // Only update UI if we successfully saved to cache
      if (updateResult && updateResult.length > 0) {
        setYoutubeData({
          title: videoTitle,
          thumbnail: videoThumbnail,
          url: videoUrl,
          views,
          isLive,
          liveViewers,
          channelUrl,
        });
      }
    } catch (error) {
      console.error('Background YouTube fetch failed:', error);
    }
  };

  // Fetch podcast data - with 12-hour caching
  const fetchPodcastData = async (rssUrl: string, podcastName: string, talentId: string, manualPlatformLinks?: Record<string, string>) => {
    if (fetchingPodcast || podcastFetchedRef.current) {
      console.log('Podcast fetch already in progress or completed, skipping');
      return;
    }
    
    console.log('Fetching podcast data for:', rssUrl, 'with manual links:', manualPlatformLinks);
    setFetchingPodcast(true);
    podcastFetchedRef.current = true;
    
    try {
      // Step 1: Check cache first (12-hour TTL)
      const { data: cachedData } = await supabase
        .from('podcast_cache')
        .select('*')
        .eq('podcast_rss_url', rssUrl)
        .single();
      
      const TWELVE_HOURS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
      const cacheIsValid = cachedData?.last_checked_at && 
        (Date.now() - new Date(cachedData.last_checked_at).getTime()) < TWELVE_HOURS;
      
      if (cacheIsValid && cachedData) {
        console.log('Using cached podcast data (< 12 hours old)');
        
        const cachedPodcastData = {
          title: cachedData.latest_episode_title || 'Latest Episode',
          description: cachedData.latest_episode_description || '',
          thumbnail: cachedData.latest_episode_thumbnail || '',
          url: cachedData.latest_episode_url || rssUrl,
          audioUrl: cachedData.latest_episode_audio_url || '',
          pubDate: cachedData.latest_episode_pub_date || '',
          duration: cachedData.latest_episode_duration || '',
          views: undefined,
          podcastName: cachedData.podcast_name || podcastName,
          feedUrl: rssUrl,
          // Merge cached links with manual links (manual links take priority)
          listenLinks: {
            ...(cachedData.listen_links || {}),
            ...(manualPlatformLinks || {}),
          },
        };
        
        setPodcastData(cachedPodcastData);
        setFetchingPodcast(false);
        return;
      }
      
      console.log('Cache miss or stale, fetching fresh podcast data...');
      
      // Step 2: Fetch fresh data from RSS
      let text = '';
      
      // Try multiple CORS proxies in order
      const proxies = [
        `https://api.allorigins.win/raw?url=${encodeURIComponent(rssUrl)}`,
        `https://corsproxy.io/?${encodeURIComponent(rssUrl)}`,
        rssUrl, // Try direct fetch last
      ];
      
      for (const proxyUrl of proxies) {
        try {
          console.log('Trying proxy:', proxyUrl.substring(0, 50) + '...');
          const response = await fetch(proxyUrl, {
            signal: AbortSignal.timeout(10000), // 10 second timeout
          });
          
          if (response.ok) {
            text = await response.text();
            console.log('Successfully fetched RSS, length:', text.length);
            break;
          }
        } catch (fetchErr) {
          console.warn('Fetch attempt failed:', fetchErr);
          continue;
        }
      }
      
      if (!text) {
        console.error('All fetch attempts failed for podcast RSS');
        return;
      }

      const parser = new DOMParser();
      const xml = parser.parseFromString(text, 'text/xml');
      
      // Check for parse errors
      const parseError = xml.querySelector('parsererror');
      if (parseError) {
        console.error('RSS parse error:', parseError.textContent);
        return;
      }

      const channel = xml.querySelector('channel');
      if (!channel) {
        console.error('No channel found in RSS feed');
        return;
      }

      // Get podcast title from feed if not provided
      const feedTitle = channel.querySelector('title')?.textContent || podcastName;
      
      // Get the latest episode (first item)
      const item = channel.querySelector('item');
      if (!item) {
        console.error('No episodes found in podcast feed');
        return;
      }

      const title = item.querySelector('title')?.textContent || 'Latest Episode';
      const description = item.querySelector('description')?.textContent || '';
      const pubDate = item.querySelector('pubDate')?.textContent || '';
      
      // Get episode link - try different common formats
      let episodeUrl = item.querySelector('link')?.textContent || '';
      
      // Try to get enclosure URL (direct audio link) - this is the actual MP3 file
      const enclosure = item.querySelector('enclosure');
      const audioUrl = enclosure?.getAttribute('url') || '';
      
      console.log('Episode URLs:', { episodeUrl, audioUrl });
      
      // Use audio URL for episode URL if page link is missing
      if (!episodeUrl && audioUrl) {
        episodeUrl = audioUrl;
      }
      
      // Get thumbnail - try iTunes image first, then channel image
      let thumbnail = '';
      
      // Try iTunes image on the item
      const itunesImage = item.querySelector('image');
      if (itunesImage) {
        thumbnail = itunesImage.getAttribute('href') || itunesImage.textContent || '';
      }
      
      // Try media:thumbnail
      if (!thumbnail) {
        const mediaThumbnail = item.querySelector('thumbnail');
        if (mediaThumbnail) {
          thumbnail = mediaThumbnail.getAttribute('url') || '';
        }
      }
      
      // Fall back to channel image
      if (!thumbnail) {
        const channelImage = channel.querySelector('image > url');
        if (channelImage) {
          thumbnail = channelImage.textContent || '';
        }
      }
      
      // Try iTunes channel image
      if (!thumbnail) {
        const itunesChannelImage = channel.querySelector('image[href]');
        if (itunesChannelImage) {
          thumbnail = itunesChannelImage.getAttribute('href') || '';
        }
      }
      
      // Get duration if available
      const durationEl = item.querySelector('duration');
      const duration = durationEl?.textContent || '';

      // Format listen count from enclosure length (bytes) - rough estimate
      let views = null;
      const enclosureLength = enclosure?.getAttribute('length');
      if (enclosureLength) {
        const bytes = parseInt(enclosureLength);
        // Rough estimate: assume avg 50MB file = 1000 downloads
        const estimatedListens = Math.floor(bytes / 50000);
        if (estimatedListens > 0) {
          views = estimatedListens >= 1000 
            ? `${(estimatedListens / 1000).toFixed(1)}K` 
            : `${estimatedListens}`;
        }
      }

      // Extract platform links from RSS feed
      const listenLinks: { spotify?: string; apple?: string; youtube?: string; google?: string; [key: string]: string | undefined } = {};
      
      // Look for links in channel (podcast-level links)
      const allLinks = Array.from(channel.querySelectorAll('link'));
      allLinks.forEach(link => {
        const href = link.getAttribute('href') || link.textContent || '';
        const lowerHref = href.toLowerCase();
        
        if (lowerHref.includes('spotify.com')) {
          listenLinks.spotify = href;
        } else if (lowerHref.includes('apple.com/podcast') || lowerHref.includes('podcasts.apple.com')) {
          listenLinks.apple = href;
        } else if (lowerHref.includes('youtube.com') || lowerHref.includes('youtu.be')) {
          listenLinks.youtube = href;
        } else if (lowerHref.includes('podcasts.google.com')) {
          listenLinks.google = href;
        }
      });
      
      // Also check atom:link elements with rel attribute
      const atomLinks = Array.from(channel.querySelectorAll('atom\\:link, [rel]'));
      atomLinks.forEach(link => {
        const href = link.getAttribute('href') || '';
        const lowerHref = href.toLowerCase();
        
        if (lowerHref.includes('spotify.com') && !listenLinks.spotify) {
          listenLinks.spotify = href;
        } else if ((lowerHref.includes('apple.com/podcast') || lowerHref.includes('podcasts.apple.com')) && !listenLinks.apple) {
          listenLinks.apple = href;
        } else if ((lowerHref.includes('youtube.com') || lowerHref.includes('youtu.be')) && !listenLinks.youtube) {
          listenLinks.youtube = href;
        } else if (lowerHref.includes('podcasts.google.com') && !listenLinks.google) {
          listenLinks.google = href;
        }
      });
      
      console.log('Extracted listen links:', listenLinks);

      const podcastDataObj = {
        title,
        description: description.replace(/<[^>]*>/g, '').substring(0, 200), // Strip HTML and truncate
        thumbnail,
        url: episodeUrl || rssUrl,
        audioUrl: audioUrl || episodeUrl, // Direct audio link for player
        pubDate,
        duration,
        views: views || undefined,
        podcastName: feedTitle,
        feedUrl: rssUrl,
        listenLinks: Object.keys(listenLinks).length > 0 ? listenLinks : undefined,
      };
      
      console.log('Successfully parsed podcast data:', podcastDataObj);
      setPodcastData(podcastDataObj);
      
      // Enhance with PodcastIndex API platform links (runs in background)
      let enhancedLinks = { ...listenLinks };
      try {
        const { data: platformData, error: platformError } = await supabase.functions.invoke('get-podcast-platforms', {
          body: { feedUrl: rssUrl }
        });
        
        if (!platformError && platformData?.platforms) {
          console.log('PodcastIndex platform links:', platformData.platforms);
          // Merge with existing links (manual links take highest priority)
          enhancedLinks = {
            ...listenLinks,
            ...platformData.platforms,
            ...(manualPlatformLinks || {}), // Manual links override all
          };
          
          if (Object.keys(enhancedLinks).length > 0) {
            setPodcastData(prev => prev ? {
              ...prev,
              listenLinks: enhancedLinks
            } : prev);
            console.log('Enhanced with platform links:', enhancedLinks);
          }
        } else if (manualPlatformLinks && Object.keys(manualPlatformLinks).length > 0) {
          // Even if API call failed, use manual links
          enhancedLinks = {
            ...listenLinks,
            ...manualPlatformLinks,
          };
          setPodcastData(prev => prev ? {
            ...prev,
            listenLinks: enhancedLinks
          } : prev);
          console.log('Using manual platform links:', enhancedLinks);
        }
      } catch (platformError) {
        console.warn('PodcastIndex API call failed (non-blocking):', platformError);
      }
      
      // Step 3: Update cache with fresh data (upsert)
      try {
        const cacheData = {
          talent_id: talentId,
          podcast_rss_url: rssUrl,
          podcast_name: feedTitle,
          latest_episode_title: title,
          latest_episode_description: description.replace(/<[^>]*>/g, '').substring(0, 500),
          latest_episode_thumbnail: thumbnail,
          latest_episode_url: episodeUrl || rssUrl,
          latest_episode_audio_url: audioUrl,
          latest_episode_duration: duration,
          latest_episode_pub_date: pubDate,
          listen_links: Object.keys(enhancedLinks).length > 0 ? enhancedLinks : null,
          last_checked_at: new Date().toISOString(),
        };
        
        // Use upsert to insert or update based on talent_id + podcast_rss_url (unique constraint)
        const { error: cacheError } = await supabase
          .from('podcast_cache')
          .upsert(cacheData, { 
            onConflict: 'talent_id,podcast_rss_url',
            ignoreDuplicates: false 
          });
        
        if (cacheError) {
          console.warn('Failed to update podcast cache:', cacheError);
        } else {
          console.log('Podcast cache updated successfully');
        }
      } catch (cacheErr) {
        console.warn('Error updating podcast cache (non-blocking):', cacheErr);
      }
    } catch (error) {
      console.error('Error fetching podcast data:', error);
    } finally {
      setFetchingPodcast(false);
    }
  };

  // Track special card clicks (rumble, youtube, podcast, shoutout, service)
  const trackCardClick = (cardType: string) => {
    if (!talentProfile?.id) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const isPreview = urlParams.get('preview') === 'true';
    
    if (!isPreview) {
      // GA4 custom event for card click tracking
      (window as any).gtag?.('event', 'card_click', {
        talent_slug: username,
        card_type: cardType,
      });

      try {
        void supabase.functions.invoke('track-bio-click', {
          body: {
            talent_id: talentProfile.id,
            card_type: cardType,
            referrer: document.referrer || null,
            utm_source: urlParams.get('utm_source'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
          },
        });
      } catch (error) {
        console.error('Failed to track card click:', error);
      }
    }
  };

  // Track link clicks
  const handleLinkClick = async (link: BioLink) => {
    if (link.id && talentProfile?.id) {
      // GA4 custom event for link click tracking
      const urlParams = new URLSearchParams(window.location.search);
      const isPreview = urlParams.get('preview') === 'true';
      if (!isPreview) {
        (window as any).gtag?.('event', 'card_click', {
          talent_slug: username,
          card_type: 'link',
        });
      }

      // Update click_count on bio_links (legacy)
      await supabase
        .from('bio_links')
        .update({ click_count: (link.click_count || 0) + 1 })
        .eq('id', link.id);
      
      // Also track to bio_link_clicks for detailed analytics
      if (!isPreview) {
        try {
          void supabase.functions.invoke('track-bio-click', {
            body: {
              talent_id: talentProfile.id,
              bio_link_id: link.id,
              card_type: 'link',
              referrer: document.referrer || null,
              utm_source: urlParams.get('utm_source'),
              utm_medium: urlParams.get('utm_medium'),
              utm_campaign: urlParams.get('utm_campaign'),
            },
          });
        } catch (error) {
          console.error('Failed to track click:', error);
        }
      }
    }
  };

  // ── Follow / "Become {first}'s Fan" ───────────────────────────────────────
  // Core write — assumes currentUser is logged in. Extracted from
  // handleFollowSignup so the deferred-auth path (open AccountModal, then
  // auto-finish after the fan signs up) can re-use it without re-entering
  // the click handler. Mirrors runCheckInCore.
  const runFollowCore = useCallback(async () => {
    if (!talentProfile?.id || !currentUser?.id) return;
    setSubscribing(true);
    try {
      const { error: followError } = await supabase
        .from('talent_followers')
        .insert({ user_id: currentUser.id, talent_id: talentProfile.id });

      if (followError && !followError.message.includes('duplicate')) {
        throw followError;
      }

      if (currentUser.email) {
        try {
          await supabase.functions.invoke('capture-lead', {
            body: {
              email: currentUser.email,
              phone: currentUser.phone || null,
              source: 'bio_page',
              talent_slug: talentProfile.username || talentProfile.id,
            },
          });
        } catch (flowError) {
          console.error('Email flow enrollment error:', flowError);
        }
      }

      if (newsletterConfig) {
        try {
          await sendToNewsletterIntegration(newsletterConfig, {
            email: currentUser.email || '',
            phone: currentUser.phone || null,
            name: currentUser.full_name || '',
            talent_id: talentProfile.id,
            talent_name: bioSettings?.display_name || talentProfile?.full_name || '',
          });
        } catch (webhookError) {
          console.error('Newsletter integration error:', webhookError);
        }
      }

      setIsFollowing(true);
      if (currentUser.email) {
        localStorage.setItem('bio_subscriber_email', currentUser.email.toLowerCase());
      }
      localStorage.setItem('bio_account_verified', 'true');

      // Get (or mint) a 25% FANGIFT coupon for this fan+creator, then take
      // them to the post-registration welcome page which shows the coupon CTA.
      try {
        const { data: couponCode } = await supabase.rpc('get_or_create_fan_gift_coupon', {
          p_user_id:   currentUser.id,
          p_talent_id: talentProfile.id,
        });
        const dest = `/${talentProfile.username || talentProfile.id}/message?registered=1${couponCode ? `&coupon=${encodeURIComponent(couponCode)}` : ''}`;
        navigate(dest);
      } catch {
        // fallback — stay on page with a toast if redirect fails
        setFollowWavesKey((k) => k + 1);
        toast.success(`You're now subscribed to ${bioSettings?.display_name || talentProfile?.full_name?.split(' ')[0]}!`);
      }
    } catch (error) {
      console.error('Follow error:', error);
      toast.error('Failed to follow. Please try again.');
    } finally {
      setSubscribing(false);
    }
  }, [talentProfile, currentUser, newsletterConfig, bioSettings]);

  // Click entry. Anonymous taps open AccountModal and remember the intent
  // so the follow auto-completes once the modal closes (see effect below).
  const handleFollowSignup = useCallback(async (e?: React.SyntheticEvent) => {
    if (e && 'preventDefault' in e) e.preventDefault();
    if (!talentProfile?.id) return;
    trackCardClick('newsletter');

    if (!currentUser?.id) {
      pendingFollowRef.current = true;
      setShowAccountModal(true);
      return;
    }
    await runFollowCore();
  }, [talentProfile?.id, currentUser?.id, runFollowCore]);

  // Auto-fire deferred follow after the AccountModal has fully closed (mirror
  // of the check-in deferred effect — keeps the wave animation from playing
  // behind the auth sheet).
  useEffect(() => {
    const wasOpen = prevShowAccountModalForFollowRef.current;
    prevShowAccountModalForFollowRef.current = showAccountModal;
    if (wasOpen && !showAccountModal) {
      if (pendingFollowRef.current && currentUser?.id) {
        pendingFollowRef.current = false;
        runFollowCore();
      } else {
        pendingFollowRef.current = false;
      }
    }
  }, [showAccountModal, currentUser?.id, runFollowCore]);

  // ── "Become a Fan" exit-intent / 8s popup ────────────────────────────────
  // Triggers when an anonymous (or just-registered-but-not-following) visitor
  // either dwells for 8s or signals exit intent. Once shown OR dismissed,
  // we set a sessionStorage flag so we don't re-trigger on the same session.
  // Mobile is ~90% of traffic so we lean on signals that exist there:
  //   1. 8-second dwell timer (always)
  //   2. Fast upward scroll (mobile-friendly proxy for "I'm leaving")
  //   3. Mouse leaves the viewport from the top (desktop)
  //   4. Tab/app backgrounded via visibilitychange (mobile back/swipe-up)
  // We deliberately skip pushing a sentinel history state so we don't break
  // browser back-navigation.
  //
  // We also defer the trigger while another full-screen modal is open
  // (collab, sponsor, podcast, order overlay). When those close without
  // an action, the effect re-runs and the 8 s dwell timer naturally
  // gives the user "a few seconds" of breathing room before the popup
  // appears.
  useEffect(() => {
    if (!talentProfile?.id) return;
    if (currentUser?.id) return;
    if (isFollowing) return;
    if (bioSettings?.show_newsletter === false) return;
    if (becomeFanShownRef.current) return;
    try {
      if (sessionStorage.getItem('bio_become_fan_dismissed') === '1') return;
    } catch {}
    if (showAccountModal) return;          // already open / pending
    if (showCollabModal) return;           // collab inquiry form open
    if (showSponsorModal) return;          // sponsor inquiry form open
    if (showPodcastModal) return;          // podcast modal open
    if (orderOverlay) return;              // post-purchase video overlay open

    // Hard guard: never fire the popup when BioPage is embedded inside an
    // iframe (creator.shoutout.us dashboard, ads.shoutout.us preview, third-
    // party embeds) or when known "embedded" query params are present. This
    // single check (computed once on mount via useMemo) covers iframe
    // detection, ?source=, ?popup=0, ?embed=1, and a referrer-based fallback.
    if (isEmbeddedContext) return;


    // Defensive pathname guard: BioPage is only mounted at /:username, but
    // skip the popup anyway if we're somehow on a sub-page like
    // /:username/support, /:username/shoutout, /:username/message, etc.
    // (e.g. embed contexts that mount BioPage inside another shell).
    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      const segments = path.split('/').filter(Boolean);
      // /:username  → 1 segment. Anything else (sub-pages, /dashboard,
      // /account, /start, /collab/...) we leave alone.
      if (segments.length !== 1) return;
    }

    const trigger = (reason: string) => {
      if (becomeFanShownRef.current) return;
      becomeFanShownRef.current = true;
      // GA4-only impression ping. Intentionally NOT routed through
      // trackCardClick / track-bio-click — auto-firing the popup on
      // an 8s timer / scroll / mouseleave is not a click and was
      // polluting the per-card click counter (e.g. showing 2,183
      // "clicks" on become_fan_popup_timer that nobody actually
      // tapped). Real conversion is tracked when the user submits
      // their email (see record_bio_email_lead RPC) or finishes OTP.
      try {
        (window as any).gtag?.('event', 'become_fan_popup_shown', {
          talent_slug: username,
          reason,
        });
      } catch {}
      setAccountModalContext('becomeFan');
      setShowAccountModal(true);
    };

    const timerHandle = window.setTimeout(() => trigger('timer'), 8000);

    const onMouseLeave = (e: MouseEvent) => {
      // Heuristic: cursor leaves the document toward the address bar /
      // browser chrome. clientY <= 0 means the pointer is leaving from the
      // top of the viewport.
      if (e.clientY <= 0 && !e.relatedTarget) trigger('mouseleave');
    };
    document.addEventListener('mouseleave', onMouseLeave);

    // Fast upward scroll on mobile = "I'm trying to bail back to the top
    // and tap something else / leave". Track velocity and require a short
    // sustained burst so a normal scroll doesn't pop the modal.
    let lastY = window.scrollY;
    let lastT = performance.now();
    let upBurst = 0;
    const onScroll = () => {
      const y = window.scrollY;
      const t = performance.now();
      const dy = lastY - y; // > 0 means scrolling up
      const dt = t - lastT;
      if (dt > 0 && dy > 0) {
        const velocity = dy / (dt / 1000); // px / sec
        if (velocity > 1200) upBurst += dy;
        else if (dy < 4) upBurst = 0;
        if (upBurst > 280 && y < 600) trigger('fast_scroll_up');
      } else if (dy < 0) {
        upBurst = 0;
      }
      lastY = y;
      lastT = t;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Background / tab-switch is a strong mobile-friendly leaving signal
    // (back-swipe on iOS, app switcher, opening another tab). We only fire
    // here if the visit lasted long enough to give the popup a chance —
    // 4 s — so a fast bounce doesn't trigger.
    const visibleSince = Date.now();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && Date.now() - visibleSince > 4000) {
        trigger('visibility_hidden');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearTimeout(timerHandle);
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [
    talentProfile?.id,
    currentUser?.id,
    isFollowing,
    bioSettings?.show_newsletter,
    showAccountModal,
    showCollabModal,
    showSponsorModal,
    showPodcastModal,
    orderOverlay,
    isEmbeddedContext,
  ]);

  // When the AccountModal closes after a become-fan trigger, persist the
  // dismissal so we don't re-show it on the same session, reset the context,
  // and complete the follow so the user lands on the registered confirmation page.
  useEffect(() => {
    const wasOpen = prevShowAccountModalForBecomeFanRef.current;
    prevShowAccountModalForBecomeFanRef.current = showAccountModal;
    if (wasOpen && !showAccountModal && accountModalContext === 'becomeFan') {
      try { sessionStorage.setItem('bio_become_fan_dismissed', '1'); } catch {}
      setAccountModalContext('default');
      if (currentUser?.id) {
        runFollowCore();
      }
    }
  }, [showAccountModal, accountModalContext, currentUser?.id, runFollowCore]);

  // Get button radius class
  const getRadiusClass = () => {
    const buttonStyle = bioSettings?.button_style || 'rounded';
    if (buttonStyle === 'pill') return 'rounded-full';
    if (buttonStyle === 'square') return 'rounded-md';
    return 'rounded-xl';
  };

  // Get button style object - ALL styling done via inline styles for consistency
  const getButtonStyle = (): React.CSSProperties => {
    const buttonColor = bioSettings?.button_color || '#3b82f6';
    const cardStyle = bioSettings?.card_style || 'glass';
    
    const baseStyle: React.CSSProperties = {
      width: '100%',
      padding: '16px',
      transition: 'all 0.3s',
      display: 'block',
    };
    
    if (cardStyle === 'glass') {
      return {
        ...baseStyle,
        backgroundColor: `${buttonColor}20`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: `${buttonColor}50`,
        backdropFilter: 'blur(8px)',
      };
    } else if (cardStyle === 'solid') {
      return {
        ...baseStyle,
        backgroundColor: `${buttonColor}40`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: `${buttonColor}30`,
      };
    } else if (cardStyle === 'outline') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: buttonColor,
      };
    } else if (cardStyle === 'shadow') {
      return {
        ...baseStyle,
        backgroundColor: `${buttonColor}25`,
        boxShadow: `0 4px 20px ${buttonColor}40`,
        border: 'none',
      };
    }
    
    return {
      ...baseStyle,
      backgroundColor: `${buttonColor}20`,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: `${buttonColor}50`,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-4">
        <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
          <LinkIcon className="h-8 w-8 text-gray-600" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Bio Not Found</h1>
        <p className="text-gray-400 mb-6 text-center">This bio page doesn't exist or isn't published yet.</p>
        <a
          href="https://shoutout.us?utm=fans"
          className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
        >
          Create Your Own Bio
        </a>
      </div>
    );
  }

  const gradientDirection = bioSettings?.gradient_direction === 'to-b' ? '180deg' : '135deg';

  // Detect light backgrounds and derive contrast colors from the background hue
  const bgStartHex = (bioSettings?.gradient_start || '#0a0a0a').replace('#', '');
  const bgR = parseInt(bgStartHex.substr(0, 2), 16) || 0;
  const bgG = parseInt(bgStartHex.substr(2, 2), 16) || 0;
  const bgB = parseInt(bgStartHex.substr(4, 2), 16) || 0;
  const bgLuminance = (0.299 * bgR + 0.587 * bgG + 0.114 * bgB) / 255;
  const isLightBg = bgLuminance > 0.6;
  // Darken the background color by reducing each channel to ~25% for primary text
  const darkR = Math.round(bgR * 0.25);
  const darkG = Math.round(bgG * 0.25);
  const darkB = Math.round(bgB * 0.25);
  const darkBgColor = `rgb(${darkR}, ${darkG}, ${darkB})`;
  // Slightly lighter for secondary text (~40%)
  const midR = Math.round(bgR * 0.4);
  const midG = Math.round(bgG * 0.4);
  const midB = Math.round(bgB * 0.4);
  const midBgColor = `rgb(${midR}, ${midG}, ${midB})`;
  const lightTextPrimary = isLightBg ? darkBgColor : '#ffffff';
  const lightTextSecondary = isLightBg ? midBgColor : 'rgba(255,255,255,0.6)';
  // Accent color: saturated, ~55% brightness version of the background for headings/labels
  const accentR = Math.round(Math.min(255, bgR * 0.45 + (bgR > bgG && bgR > bgB ? 30 : 0)));
  const accentG = Math.round(Math.min(255, bgG * 0.35));
  const accentB = Math.round(Math.min(255, bgB * 0.5 + (bgB > bgR && bgB > bgG ? 30 : 0)));
  const lightAccentColor = `rgb(${accentR}, ${accentG}, ${accentB})`;
  const lightDividerColor = isLightBg ? `rgba(${darkR},${darkG},${darkB},0.15)` : 'rgba(255,255,255,0.1)';

  // Get name from talent profile, then temp_full_name (pre-onboarding), then user, then bio settings, then fallback
  const userName = (talentProfile as any)?.users?.full_name;
  const userAvatar = (talentProfile as any)?.users?.avatar_url;
  const displayName = (
    (talentProfile?.full_name || '').trim() ||
    (talentProfile?.temp_full_name || '').trim() ||
    (userName || '').trim() ||
    (bioSettings?.display_name || '').trim() ||
    'Creator'
  ).replace(/\s+/g, ' ');
  const profileImage = talentProfile?.temp_avatar_url || bioSettings?.profile_image_url || userAvatar;

  // ShoutOut card style - derived from the creator's button_color so it feels
  // on-brand, but kicked up a notch (stronger tint, slight accent shift) so it
  // still stands out from regular link cards.
  const shoutoutBase = bioSettings?.button_color || '#3b82f6';
  const shoutoutAccent = bioSettings?.accent_color || shoutoutBase;
  const shoutoutCardStyle: React.CSSProperties = {
    background: `linear-gradient(135deg, ${shoutoutBase}55, ${shoutoutAccent}40)`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `${shoutoutBase}80`,
    boxShadow: `0 4px 20px ${shoutoutBase}35`,
    width: '100%',
    padding: 0,
  };

  // Custom "Show Up Now" card for Kaylee Campbell only
  const ShowUpNowCard = talentProfile?.username === 'kayleecampbell' ? (
    <div key="showupnow-card" className="rounded-2xl overflow-hidden border border-emerald-500/30 bg-gradient-to-r from-emerald-500/15 to-teal-500/15">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="h-5 w-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
          </svg>
          <h3 className="text-white font-semibold text-sm">Find your next City Council meeting</h3>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            placeholder="Enter zip code"
            value={showUpZip}
            onChange={(e) => setShowUpZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && showUpZip.length === 5) {
                trackCardClick('showupnow');
                window.open(`https://www.showupnow.us/?zip=${showUpZip}`, '_blank');
              }
            }}
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30"
          />
          <button
            onClick={() => {
              if (showUpZip.length === 5) {
                trackCardClick('showupnow');
                window.open(`https://www.showupnow.us/?zip=${showUpZip}`, '_blank');
              }
            }}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Search
          </button>
        </div>
        <p className="text-gray-400 text-[10px] mt-2">Powered by <a href="https://www.showupnow.us" target="_blank" rel="noopener noreferrer" onClick={() => trackCardClick('powered_by_showupnow')} className="text-emerald-400 hover:underline">ShowUpNow.us</a></p>
      </div>
    </div>
  ) : null;

  // Meghan Elinor TWC promo card
  const MeghanTWCCard = talentProfile?.username === 'meghanelinor' ? (
    <a
      key="meghan-twc-card"
      href="https://www.twc.health/pages/meghane?ref=MEGHANE&utm_content=link_in_bio&utm_medium=social&utm_source=shoutout"
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      onClick={() => trackCardClick('sponsor_ad')}
    >
      <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-all duration-300">
        <img
          src="/meghan.png"
          alt="Meghan Elinor - The Wellness Company - Save 10% Off Nutraceuticals + $30 Off Ivermectin with Code MEGHANE"
          className="w-full h-auto block"
        />
      </div>
    </a>
  ) : null;

  // Email-style multi-talent ShoutOut card — only on username='shoutout'
  const isShoutoutProfile = (username || '').toLowerCase() === 'shoutout';

  // Creator community card — only on username='shoutout'
  const CreatorCommunityCard = isShoutoutProfile ? (
    <a
      key="creator-community-card"
      href="https://creator.shoutout.us/start"
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      onClick={() => trackCardClick('creator_community')}
    >
      <div
        className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] p-5"
        style={{ background: 'linear-gradient(135deg, #059669 0%, #2563eb 50%, #7c3aed 100%)' }}
      >
        <h3 className="text-white font-bold text-base mb-1">Join the Largest Free-Speech Creator Community</h3>
        <p className="text-white/80 text-sm mb-4">Transform your audience into a monetizable engine for growth.</p>
        <div className="flex items-center gap-1 flex-wrap">
          {featuredTalentAvatars.slice(0, 8).map((t) => (
            <div key={t.id} className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
              <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
            </div>
          ))}
          {featuredTalentAvatars.length === 0 && [...Array(6)].map((_, i) => (
            <div key={i} className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex-shrink-0" />
          ))}
        </div>
      </div>
    </a>
  ) : null;

  const EmailStyleShoutOutCard = (
    <a
      key="shoutout-email-card"
      href="https://shoutout.us"
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      onClick={() => trackCardClick('shoutout')}
    >
      <div
        className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] p-5"
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #c026d3 50%, #f43f5e 100%)' }}
      >
        <h3 className="text-white font-bold text-base mb-1">Get a Personalized Video ShoutOut</h3>
        <p className="text-white/80 text-sm mb-4">From top free-speech personalities — starting at $27</p>
        <div className="flex items-center gap-1 flex-wrap">
          {featuredTalentAvatars.slice(0, 8).map((t) => (
            <div key={t.id} className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
              <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
            </div>
          ))}
          {featuredTalentAvatars.length === 0 && [...Array(6)].map((_, i) => (
            <div key={i} className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex-shrink-0" />
          ))}
          <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[10px] font-semibold">+50</span>
          </div>
        </div>
      </div>
    </a>
  );

  // MacBook Neo giveaway card — only shown on megainspo + mericainspo
  const neoCountdown = (() => {
    const msLeft = NEO_END_DATE.getTime() - Date.now();
    if (msLeft <= 0) return null;
    const days = Math.floor(msLeft / 86400000);
    const hrs  = Math.floor((msLeft % 86400000) / 3600000);
    const mins = Math.floor((msLeft % 3600000) / 60000);
    if (days > 0) return `${days}d ${hrs}h left`;
    if (hrs  > 0) return `${hrs}h ${mins}m left`;
    return `${mins}m left`;
  })();
  const MacBookNeoCard = isNeoProfile && neoCountdown ? (
    <div
      key="neo-giveaway-card"
      className="relative rounded-2xl overflow-hidden cursor-pointer select-none"
      style={{ border: '2px solid rgba(250,204,21,0.4)', background: 'linear-gradient(135deg,rgba(15,10,5,0.97) 0%,rgba(30,20,5,0.97) 100%)' }}
      onClick={neoEntered ? undefined : handleNeoEntry}
    >
      {/* Hero image */}
      <div className="relative w-full overflow-hidden" style={{ height: 180 }}>
        <img
          src="https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/giveaway/macbook-neo.png"
          alt="MacBook Neo"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 30%' }}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.85) 100%)' }} />
        {/* Countdown pill */}
        <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: 'rgba(0,0,0,0.7)', color: '#facc15', border: '1px solid rgba(250,204,21,0.4)' }}>
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          {neoCountdown}
        </div>
      </div>

      {/* Content row */}
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-white font-bold text-base leading-tight">Win a MacBook Neo</p>
          <p className="text-yellow-300/80 text-xs mt-0.5">Enter for free — winner picked May 22nd</p>
        </div>
        {/* CTA / entered state */}
        <div className="relative flex-shrink-0">
          {neoEntered ? (
            <div className="relative flex items-center justify-center w-14 h-14">
              {/* Wave rings (same pattern as check-in) */}
              {neoWavesKey > 0 && (
                <span key={neoWavesKey} aria-hidden className="pointer-events-none absolute inset-0">
                  {[0, 0.18, 0.36].map((delay, i) => (
                    <span
                      key={i}
                      className="absolute inset-0 rounded-full border-[3px]"
                      style={{
                        borderColor: '#facc15',
                        boxShadow: '0 0 24px #facc15, 0 0 48px #facc1566',
                        animation: 'bioCheckinWave 1.6s ease-out forwards',
                        animationDelay: `${delay}s`,
                      }}
                    />
                  ))}
                </span>
              )}
              <div className="w-14 h-14 rounded-full flex flex-col items-center justify-center text-center" style={{ background: 'rgba(250,204,21,0.15)', border: '2px solid #facc15' }}>
                <svg className="w-5 h-5 text-yellow-400 mb-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <span className="text-yellow-300 text-[9px] font-bold leading-tight">Entered!</span>
              </div>
            </div>
          ) : (
            <button
              className="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 hover:scale-105 active:scale-95"
              style={{ background: '#facc15', color: '#0a0700' }}
            >
              Enter Now
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slideIn { animation: slideIn 0.4s ease-out; }
      `}</style>
    <div 
      className="min-h-screen"
      style={{
        background: `linear-gradient(${gradientDirection}, ${bioSettings?.gradient_start || '#0a0a0a'}, ${bioSettings?.gradient_end || '#1a1a2e'})`
      }}
    >
      <div className="max-w-lg mx-auto px-4 py-8 min-h-screen flex flex-col relative">
        {/* ShoutOut Logo - Top Left Corner */}
        <a 
          href="https://shoutout.us/creators" 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={() => trackCardClick('shoutout_logo')}
          className="absolute top-[18px] left-4 z-10 opacity-45 hover:opacity-100 transition-opacity"
        >
          <img
            src={`${process.env.PUBLIC_URL}/shoutoutwhite.png`}
            alt="ShoutOut"
            className="h-5 w-auto"
            style={isLightBg ? { filter: 'brightness(0.3) sepia(1) saturate(0.5)', opacity: 0.7 } : undefined}
          />
        </a>

        {/* Balance badge moved into the floating bottom bar, between the
            Stay Connected / Support button and the user's avatar. */}

        {/* Top Fans leaderboard — top right, faded, minimal.
            Shows top 15 with only 4 rows in the viewport at a time; the list
            auto-scrolls vertically like a marquee. */}
        {showTopFans && (leaderboard.length > 0 || fanRank) && (() => {
          const ROW_PX = 15;
          const VISIBLE_ROWS = 4;
          const SCROLL_ROWS = VISIBLE_ROWS - 1; // #1 is pinned; remaining 3 rows scroll.
          // Rank badges take on the creator's own accent color (button_color),
          // gradually fading toward neutral as the rank falls — so the
          // leaderboard "blends" into the rest of the bio page UI.
          const accent = bioSettings?.button_color || '#22c55e';
          const accentText = getContrastTextColor(accent);
          const toRgba = (hex: string, alpha: number): string => {
            const h = hex.replace('#', '');
            if (h.length !== 6) return hex;
            const r = parseInt(h.slice(0, 2), 16);
            const g = parseInt(h.slice(2, 4), 16);
            const b = parseInt(h.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          };
          const badgeStyle = (rank: number) => {
            if (rank === 1) return { bg: accent, color: accentText };
            if (rank === 2) return { bg: toRgba(accent, 0.70), color: accentText };
            if (rank === 3) return { bg: toRgba(accent, 0.48), color: accentText };
            if (rank <= 6) return { bg: toRgba(accent, 0.28), color: lightTextPrimary };
            return { bg: toRgba(accent, 0.14), color: lightTextPrimary };
          };
          const pinned = leaderboard.find((f) => f.rank === 1) || null;
          const rest = leaderboard.filter((f) => f.rank !== 1);
          const animationEnabled = rest.length > SCROLL_ROWS;
          const scrollDurationS = Math.max(rest.length * 2.2, 8);
          const renderRow = (f: LeaderboardRow, key: string) => {
            const b = badgeStyle(f.rank);
            return (
              <li
                key={key}
                className="flex items-center justify-end gap-1.5 truncate tabular-nums"
                style={{ height: ROW_PX, color: lightTextPrimary, opacity: isLightBg ? 0.72 : 0.7 }}
              >
                <span className="text-[10px] truncate">{f.display_name}</span>
                <span
                  className="inline-flex items-center justify-center rounded-full font-bold flex-shrink-0"
                  style={{ width: 14, height: 14, fontSize: 8.5, backgroundColor: b.bg, color: b.color }}
                  aria-label={`Rank ${f.rank}`}
                >
                  {f.rank}
                </span>
              </li>
            );
          };
          return (
            <div
              className="absolute right-4 z-10 text-right leading-tight"
              style={{ top: '18px', maxWidth: '170px', width: 'max-content' }}
            >
              {leaderboard.length > 0 && (
                <>
                  <div
                    className="text-[9px] font-semibold uppercase tracking-[0.12em] mb-1 whitespace-nowrap"
                    style={{ color: lightTextPrimary, opacity: isLightBg ? 0.55 : 0.45 }}
                  >
                    {(() => {
                      const first = (displayName || 'Creator').split(' ')[0];
                      return `${first}${first.endsWith('s') ? "'" : "'s"} Top Fans`;
                    })()}
                  </div>
                  <style>{`
                    @keyframes bioFansMarquee {
                      from { transform: translateY(0); }
                      to   { transform: translateY(-50%); }
                    }
                    .bio-fans-track:hover { animation-play-state: paused; }
                  `}</style>
                  {pinned && (
                    <ol>
                      {renderRow(pinned, `pinned-${pinned.user_id || pinned.fan_email || 1}`)}
                    </ol>
                  )}
                  <div
                    className="overflow-hidden"
                    style={{
                      height: ROW_PX * SCROLL_ROWS,
                      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 18%, #000 82%, transparent 100%)',
                      maskImage: 'linear-gradient(to bottom, transparent 0, #000 18%, #000 82%, transparent 100%)',
                    }}
                  >
                    <ol
                      className="bio-fans-track"
                      style={{
                        animation: animationEnabled ? `bioFansMarquee ${scrollDurationS}s linear infinite` : undefined,
                      }}
                    >
                      {(animationEnabled ? [...rest, ...rest] : rest).map((f, i) =>
                        renderRow(f, `${f.user_id || f.fan_email || f.rank}-${i}`),
                      )}
                    </ol>
                  </div>
                </>
              )}
              {fanRank && fanRank.rank > 0 && (
                <div
                  className="text-[10px] mt-1.5 font-medium"
                  style={{ color: lightTextPrimary, opacity: isLightBg ? 0.75 : 0.72 }}
                >
                  Your rank: #{fanRank.rank} ({fanRank.points} pts)
                </div>
              )}
            </div>
          );
        })()}

        {/* Profile Header */}
        <div className="text-center mb-8">
          {/* Profile Coin: flips every 4s (holds open 3s) to reveal the
              Check-In face until the fan checks in, then switches to a live
              countdown until their next available check-in. */}
          {showTopFans ? (() => {
            const waveColor = bioSettings?.button_color || '#22c55e';
            const cooldownMs = fanRank?.next_checkin_at ? new Date(fanRank.next_checkin_at).getTime() - nowTs : 0;
            const canCheckIn = cooldownMs <= 0;
            const fmtCountdown = (ms: number) => {
              const s = Math.max(0, Math.floor(ms / 1000));
              const h = Math.floor(s / 3600);
              const m = Math.floor((s % 3600) / 60);
              const sec = s % 60;
              const pad = (n: number) => n.toString().padStart(2, '0');
              return `${pad(h)}:${pad(m)}:${pad(sec)}`;
            };
            return (
              <div className="relative w-24 h-24 mx-auto mb-4" style={{ perspective: '1000px' }}>
                <style>{`
                  @keyframes bioCoinFlip { from { transform: rotateY(0deg); } to { transform: rotateY(180deg); } }
                  @keyframes bioCheckinWave {
                    0%   { transform: scale(1);    opacity: 0.75; }
                    60%  { opacity: 0.35; }
                    100% { transform: scale(3.6); opacity: 0; }
                  }
                `}</style>
                <div
                  className="relative w-full h-full transition-transform duration-[900ms]"
                  style={{ transformStyle: 'preserve-3d', transform: coinFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
                >
                  {/* Front — original profile photo. Whole circle triggers check-in. */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={canCheckIn ? 'Check in to support this creator' : 'Check in cooldown active'}
                    className="absolute inset-0 rounded-full overflow-hidden border-2 border-white/20 shadow-xl cursor-pointer"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      pointerEvents: coinFlipped ? 'none' : 'auto',
                    }}
                    onClick={handleCheckIn}
                  >
                    {profileImage ? (
                      <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl text-white font-bold">
                        {displayName[0]}
                      </div>
                    )}
                  </div>
                  {/* Back — same photo glassed out with Check In CTA. Whole circle triggers check-in. */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={canCheckIn ? 'Check in to support this creator' : 'Check in cooldown active'}
                    className="absolute inset-0 rounded-full overflow-hidden border-2 border-white/20 shadow-xl cursor-pointer"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      pointerEvents: coinFlipped ? 'auto' : 'none',
                    }}
                    onClick={handleCheckIn}
                  >
                    {profileImage ? (
                      <img src={profileImage} alt="" className="w-full h-full object-cover" style={{ filter: 'blur(4px) saturate(1.1)', transform: 'scale(1.15)' }} />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500" />
                    )}
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 pointer-events-none"
                      style={{ backgroundColor: 'rgba(0,0,0,0.38)', backdropFilter: 'blur(2px)' }}
                    >
                      <span
                        className="px-3 py-1 rounded-full text-[11px] font-bold shadow tabular-nums"
                        style={{ backgroundColor: waveColor, color: getContrastTextColor(waveColor), opacity: checkingIn || !canCheckIn ? 0.9 : 1 }}
                      >
                        {checkingIn ? '...' : canCheckIn ? 'Check In' : fmtCountdown(cooldownMs)}
                      </span>
                      <span className="text-[8px] font-medium text-white/90 mt-1 leading-tight px-1">
                        {canCheckIn ? 'daily to move up the leader board.' : 'until your next check-in'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Waves on successful check-in */}
                {checkInWavesKey > 0 && (
                  <span key={checkInWavesKey} aria-hidden className="pointer-events-none absolute inset-0">
                    {[0, 0.18, 0.36].map((delay, i) => (
                      <span
                        key={i}
                        className="absolute inset-0 rounded-full border-[3px]"
                        style={{
                          borderColor: waveColor,
                          boxShadow: `0 0 24px ${waveColor}, 0 0 48px ${waveColor}66`,
                          animation: 'bioCheckinWave 1.6s ease-out forwards',
                          animationDelay: `${delay}s`,
                        }}
                      />
                    ))}
                  </span>
                )}

              </div>
            );
          })() : (
            <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white/20 shadow-xl">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl text-white font-bold">
                  {displayName[0]}
                </div>
              )}
            </div>
          )}

          {/* Name - Always use full_name from talent profile */}
          <h1 className="text-2xl font-bold mb-1 flex items-center justify-center gap-2" style={{ color: lightTextPrimary }}>
            {displayName}
          </h1>

          {/* Support/Donate Badges - Under Name */}
          {(() => {
            const donateLinks = links.filter(l => l.link_type === 'donate' && l.is_active);
            const hasSupportEnabled = (talentProfile as any)?.support_enabled;
            if (donateLinks.length === 0 && !hasSupportEnabled) return null;

            // If the creator has enabled native support AND has 2+ external donation
            // links, collapse the external ones behind the native "Support" badge.
            // The hidden links will instead be rendered on /:username/support as
            // "Other ways to support {firstName}". A single external link stays
            // visible here since there's nothing to declutter yet.
            const collapseDonateLinks = hasSupportEnabled && donateLinks.length >= 2;
            const visibleDonateLinks = collapseDonateLinks ? [] : donateLinks;
            
            // SVG icons for each platform
            const getPlatformDisplay = (url: string, customTitle?: string): { icon: React.ReactNode; name: string; color: string } | null => {
              const lower = url.toLowerCase();
              
              if (lower.includes('venmo.com')) {
                return {
                  name: customTitle || 'Venmo',
                  color: '#3D95CE',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#3D95CE">
                      <path d="M19.5 2c.914 1.523 1.323 3.094 1.323 5.074 0 6.326-5.4 14.548-9.78 20.346H3.24L0 3.46l7.76-.714 1.64 13.13C10.84 13.79 12.92 8.82 12.92 5.39c0-1.84-.36-3.13-.84-4.13L19.5 2z"/>
                    </svg>
                  )
                };
              }
              
              if (lower.includes('paypal')) {
                return {
                  name: customTitle || 'PayPal',
                  color: '#00457C',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#00457C">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                    </svg>
                  )
                };
              }
              
              if (lower.includes('cash.app')) {
                return {
                  name: customTitle || 'Cash App',
                  color: '#00D54B',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#00D54B">
                      <path d="M3.5 3.5v17h17v-17h-17zm9.25 14.5h-1.5v-1.5h-2v-1.5h3.5v-1h-2a1.5 1.5 0 0 1-1.5-1.5v-2a1.5 1.5 0 0 1 1.5-1.5h.5V7.5h1.5V9h2v1.5h-3.5v1h2a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5h-.5V18z"/>
                    </svg>
                  )
                };
              }
              
              if (lower.includes('ko-fi.com')) {
                return {
                  name: customTitle || 'Ko-fi',
                  color: '#FF5E5B',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF5E5B">
                      <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
                    </svg>
                  )
                };
              }
              
              if (lower.includes('buymeacoffee.com')) {
                return {
                  name: customTitle || 'Buy Me a Coffee',
                  color: '#FFDD00',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FFDD00">
                      <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.683-6.663 1.024-9.995l.215-2.087a.484.484 0 01.39-.426c.402-.078.787-.212 1.074-.518.455-.488.546-1.124.385-1.766zm-1.478.772c-.145.137-.363.201-.578.233-2.416.359-4.866.54-7.308.46-1.748-.06-3.477-.254-5.207-.498-.17-.024-.353-.055-.47-.18-.22-.236-.111-.71-.054-.995.052-.26.152-.609.463-.646.484-.057 1.046.148 1.526.22.577.088 1.156.159 1.737.212 2.48.226 5.002.19 7.472-.14.45-.06.899-.13 1.345-.21.399-.072.84-.206 1.08.206.166.281.188.657.162.974a.544.544 0 01-.169.364z"/>
                    </svg>
                  )
                };
              }
              
              if (lower.includes('patreon.com')) {
                return {
                  name: customTitle || 'Patreon',
                  color: '#FF424D',
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF424D">
                      <path d="M0 .48v23.04h4.22V.48zm15.385 0c-4.764 0-8.641 3.88-8.641 8.65 0 4.755 3.877 8.623 8.641 8.623 4.75 0 8.615-3.868 8.615-8.623C24 4.36 20.136.48 15.385.48z"/>
                    </svg>
                  )
                };
              }
              
              return null;
            };
            
            return (
              <div className="flex justify-center gap-2 mt-2 mb-1 flex-wrap">
                {hasSupportEnabled && (
                  isMonthlySupporter ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={isLightBg
                      ? { background: darkBgColor, borderColor: darkBgColor }
                      : { background: 'rgba(234, 179, 8, 0.15)', borderColor: 'rgba(234, 179, 8, 0.3)' }
                    }>
                      <span className="text-sm">⭐</span>
                      <span className={`text-xs font-medium ${isLightBg ? 'text-white' : 'text-yellow-300'}`}>You're a supporter | {supporterMonths} {supporterMonths === 1 ? 'month' : 'months'}</span>
                    </span>
                  ) : (
                    <a
                      href={`/${talentProfile?.username || talentProfile?.id}/support`}
                      onClick={() => trackCardClick('support')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md transition-all hover:scale-105 border text-sm"
                      style={isLightBg
                        ? { background: darkBgColor, borderColor: darkBgColor }
                        : { background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.2)' }
                      }
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" style={{ color: isLightBg ? '#ffffff' : 'rgba(255,255,255,0.85)' }}>
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                      <span className="text-xs font-medium" style={{ color: isLightBg ? '#ffffff' : 'rgba(255,255,255,0.9)' }}>Support {displayName.startsWith('The ') || displayName.startsWith('Chicks') ? displayName : displayName.split(' ')[0]}</span>
                    </a>
                  )
                )}
                {visibleDonateLinks.map((link) => {
                  const platform = getPlatformDisplay(link.url || '', link.title || undefined);
                  const displayTitle = link.title || (platform?.name) || 'Support';
                  
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleLinkClick(link)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md transition-all hover:scale-105 border text-sm"
                      style={isLightBg
                        ? { background: darkBgColor, borderColor: darkBgColor }
                        : { background: 'rgba(255, 255, 255, 0.1)', borderColor: platform ? platform.color + '40' : 'rgba(255, 255, 255, 0.2)' }
                      }
                    >
                      {platform?.icon}
                      <span className="text-xs font-medium" style={{ color: isLightBg ? '#ffffff' : 'rgba(255,255,255,0.9)' }}>{displayTitle}</span>
                    </a>
                  );
                })}
              </div>
            );
          })()}

          {/* One-liner */}
          {bioSettings?.one_liner && (
            <p className="text-gray-300 mt-3 max-w-sm mx-auto">
              {bioSettings.one_liner}
            </p>
          )}

          {/* Stay Connected moved to floating bottom bar */}
        </div>

        {/* Links */}
        <div className="flex-1 space-y-4">
          {/* Check if any stream cards will be shown */}
          {(() => {
            const hasRumble = talentProfile?.rumble_handle && bioSettings && bioSettings.show_rumble_card !== false && rumbleData?.thumbnail;
            const hasYoutube = talentProfile?.youtube_handle && bioSettings && bioSettings.show_youtube_card !== false;
            const hasPodcast = talentProfile?.podcast_rss_url && bioSettings && bioSettings.show_podcast_card !== false;
            const hasAnyStreamCard = hasRumble || hasYoutube || hasPodcast;
            
            // Per-talent promo badge config (coupon auto-applied on their /shoutout page)
            const talentPromos: Record<string, { badge: string; couponCode: string }> = {
              'a118b8ef-aebc-4b73-8170-ff60dc2eeb48': { badge: 'First 50 get 35% off', couponCode: 'ISABELLA65' },
            };
            const activePromo = talentProfile?.id ? talentPromos[talentProfile.id] : null;

            // ShoutOut Card Component — creators can hide it via bio_settings
            const ShoutOutCardComponent = bioSettings && bioSettings.show_shoutout_card !== false ? (
              isShoutoutProfile ? <>{CreatorCommunityCard}{EmailStyleShoutOutCard}</> : (
              <a
                key="shoutout-card"
                href={`/${talentProfile?.username || talentProfile?.id}/shoutout${activePromo ? `?promo=${activePromo.couponCode}` : ''}`}
                className="block"
                onClick={() => trackCardClick('shoutout')}
              >
                <div
                  className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] relative"
                  style={shoutoutCardStyle}
                >
                  {activePromo && (
                    <div className="absolute top-0 right-0 z-10">
                      <div className="px-2.5 py-1 text-[10px] font-bold text-white rounded-bl-lg" style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}>
                        🔥 {activePromo.badge}
                      </div>
                    </div>
                  )}
                  <div className="flex items-stretch">
                    <div className="w-20 flex-shrink-0 relative overflow-hidden">
                      {profileImage ? (
                        <img src={profileImage} alt={displayName} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                          {displayName[0]}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4 flex flex-col justify-center">
                        <p className="text-sm font-bold leading-snug" style={{ color: lightTextPrimary }}>
                          Want {displayName.split(' ')[0]} to Record a Personal Video Just for You?
                        </p>

                        {randomReview ? (
                          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${lightDividerColor}` }}>
                            <div className="flex items-center gap-1 mb-0.5">
                              {[...Array(5)].map((_, i) => (
                                i < randomReview.rating ? (
                                  <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                                ) : (
                                  <StarIcon key={i} className="h-3 w-3" style={{ color: lightTextSecondary }} />
                                )
                              ))}
                            </div>
                            {randomReview.comment && (
                              <p className="text-xs line-clamp-1" style={{ color: lightTextSecondary }}>
                                "{randomReview.comment.split('\n')[0]}"
                              </p>
                            )}
                            {randomReview.users?.full_name && (
                              <p className="text-[10px] mt-0.5" style={{ color: lightTextSecondary, opacity: 0.7 }}>
                                — {randomReview.users.full_name}
                              </p>
                            )}
                          </div>
                        ) : null}
                    </div>
                  </div>
                </div>
              </a>
              )
            ) : null;
            
            return (
              <>
                {/* Stream Cards - independent cards */}
                {(() => {
                  const defaultOrder = ['rumble', 'youtube', 'podcast'];
                  const cardOrder = bioSettings?.stream_card_order || defaultOrder;
                  const fullOrder = [...cardOrder, ...defaultOrder.filter(c => !cardOrder.includes(c))];
                  
                  return fullOrder.map((cardType) => {
              // Rumble Card
              if (cardType === 'rumble' && talentProfile?.rumble_handle && bioSettings && bioSettings.show_rumble_card !== false && rumbleData?.thumbnail) {
                return (
                  <a
                    key="rumble"
                    href={rumbleData?.url || `https://rumble.com/${talentProfile.rumble_type || 'c'}/${talentProfile.rumble_handle.replace(/^@/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={() => trackCardClick('rumble')}
                  >
                    <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-[1.02]">
                      <div className="flex items-stretch">
                        <div className="w-36 h-[90px] flex-shrink-0 relative bg-black/20">
                          {rumbleData?.thumbnail ? (
                            <img src={rumbleData.thumbnail} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-600 to-emerald-600">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-white/80">
                                <path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/>
                              </svg>
                            </div>
                          )}
                          {rumbleData?.isLive && (
                            <div className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                              LIVE
                            </div>
                          )}
                        </div>
                        <div className="flex-1 px-3 py-2 flex flex-col justify-center">
                          <div className="flex items-center gap-2 mb-0.5">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-green-400">
                              <path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/>
                            </svg>
                            <span className="text-green-400 text-xs font-medium">Rumble</span>
                          </div>
                          <h3 className="text-white font-medium text-sm line-clamp-3 leading-tight">{rumbleData?.title || 'Watch on Rumble'}</h3>
                        </div>
                      </div>
                    </div>
                  </a>
                );
              }
              
              // YouTube Card
              if (cardType === 'youtube' && talentProfile?.youtube_handle && bioSettings && bioSettings.show_youtube_card !== false) {
                const youtubeCard = (
                  <a
                    key="youtube"
                    href={youtubeData?.url || `https://youtube.com/@${talentProfile.youtube_handle.replace(/^@/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={() => trackCardClick('youtube')}
                  >
                    <div className="bg-gradient-to-r from-red-500/20 to-rose-500/20 rounded-xl overflow-hidden border border-red-500/30 hover:border-red-500/50 transition-all duration-300 hover:scale-[1.02]">
                      <div className="flex items-stretch">
                        <div className="w-36 h-[90px] flex-shrink-0 relative bg-black/20">
                          {youtubeData?.thumbnail ? (
                            <img src={youtubeData.thumbnail} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-600 to-rose-600">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-white/80">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                              </svg>
                            </div>
                          )}
                          {youtubeData?.isLive && (
                            <div className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                              LIVE
                            </div>
                          )}
                        </div>
                        <div className="flex-1 px-3 py-2 flex flex-col justify-center">
                          <div className="flex items-center gap-2 mb-0.5">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-red-400">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            <span className="text-red-400 text-xs font-medium">YouTube</span>
                          </div>
                          <h3 className="text-white font-medium text-sm line-clamp-3 leading-tight">{youtubeData?.title || 'Watch on YouTube'}</h3>
                        </div>
                      </div>
                    </div>
                  </a>
                );

                if (isAdsEmbed && talentProfile?.username === 'shawnfarash') {
                  return (
                    <React.Fragment key="youtube-and-ad">
                      {youtubeCard}
                      <a
                        href="https://ads.shoutout.us"
                        target="_top"
                        rel="noopener noreferrer"
                        className="block mt-3"
                      >
                        <div className="relative bg-gradient-to-r from-green-500/30 to-emerald-500/30 rounded-xl overflow-hidden border border-green-500/40 hover:border-green-400/60 transition-all duration-300 hover:scale-[1.02]">
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg z-10">
                            20% OFF
                          </div>
                          <div className="flex items-center justify-center h-[90px]">
                            <span className="text-white font-semibold text-base tracking-wide">Your ad here</span>
                          </div>
                        </div>
                      </a>
                    </React.Fragment>
                  );
                }

                return youtubeCard;
              }
              
              // Podcast Card - show immediately if podcast RSS configured, load data async
              // Hide podcast card when Shawn's page is embedded
              if (cardType === 'podcast' && isAdsEmbed && talentProfile?.username === 'shawnfarash') {
                return null;
              }
              if (cardType === 'podcast' && talentProfile?.podcast_rss_url) {
                console.log('Rendering podcast card:', { podcastData, rss_url: talentProfile.podcast_rss_url });
                
                // Show card immediately with loading state if data isn't ready
                const isLoading = !podcastData;
                const displayData = podcastData || {
                  title: 'Loading latest episode...',
                  thumbnail: '',
                  url: talentProfile.podcast_rss_url,
                  podcastName: talentProfile.podcast_name || 'Podcast',
                  audioUrl: '',
                  views: undefined,
                  pubDate: undefined,
                  duration: undefined,
                  description: undefined,
                  feedUrl: talentProfile.podcast_rss_url,
                };
                
                const PodcastCard = () => {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        trackCardClick('podcast');
                        setShowPodcastModal(true);
                      }}
                      className="w-full text-left"
                    >
                      <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl overflow-hidden border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300">
                        <div className="flex items-center">
                          {/* Thumbnail - flush to edge */}
                          <div className="w-[50px] h-[50px] flex-shrink-0 relative bg-black/20">
                          {displayData.thumbnail ? (
                            <img src={displayData.thumbnail} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600">
                              <svg viewBox="0 0 24 24" fill="currentColor" className={`w-5 h-5 text-white/80 ${isLoading ? 'animate-pulse' : ''}`}>
                                <path d="M12 1c-6.1 0-11 4.9-11 11s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm0 20c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"/>
                                <circle cx="12" cy="12" r="2"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Content - with padding */}
                        <div className="flex-1 min-w-0 py-2 px-3">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-purple-400 flex-shrink-0">
                                <path d="M12 1c-6.1 0-11 4.9-11 11s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm0 20c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"/>
                                <circle cx="12" cy="12" r="2"/>
                              </svg>
                              <p className="text-purple-400 text-[10px] font-medium uppercase tracking-wide truncate">{displayData.podcastName}</p>
                            </div>
                            {displayData.views && (
                              <span className="text-gray-400 text-[10px] whitespace-nowrap flex-shrink-0">{displayData.views}</span>
                            )}
                          </div>
                          <h3 className={`text-white font-medium text-xs line-clamp-1 ${isLoading ? 'animate-pulse' : ''}`}>
                            {displayData.title}
                          </h3>
                        </div>
                      </div>
                    </div>
                    </button>
                  );
                };
                
                return <PodcastCard key="podcast" />;
              }
              
              return null;
                  });
                })()}
                
                {/* Sponsor ad ABOVE the ShoutOut card. */}
                {hasAnyStreamCard && sponsorAds.length > 0 && (
                  <SponsorAdCard ad={sponsorAds[0]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={0} talentFirstName={displayName} />
                )}

                {/* ShoutOut Card - After stream cards if any are enabled */}
                {hasAnyStreamCard && ShoutOutCardComponent}

                {/* Second sponsor ad — right after ShoutOut card.
                    Only renders when ShoutOutCardComponent is present
                    so we always have a real card between the two
                    sponsor ads. If the creator hid the ShoutOut card
                    we drop the second ad to keep the no-adjacent rule. */}
                {hasAnyStreamCard && ShoutOutCardComponent && sponsorAds.length > 1 && (
                  <SponsorAdCard ad={sponsorAds[1]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={1} talentFirstName={displayName} />
                )}
                
                {/* Meghan TWC Card - after ShoutOut */}
                {hasAnyStreamCard && MeghanTWCCard}
                
                {/* MacBook Neo Giveaway Card - megainspo + mericainspo */}
                {hasAnyStreamCard && MacBookNeoCard}

                {/* Show Up Now Card - Kaylee Campbell only */}
                {hasAnyStreamCard && ShowUpNowCard}
              </>
            );
          })()}


          {/* Featured Video Card */}
          {links.filter(l => l.link_type === 'video').map((link) => {
            if (!link.video_url) return null;
            
            return (
              <div key={link.id} className="rounded-2xl overflow-hidden border border-white/20">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  onClick={(e) => {
                    const video = e.currentTarget;
                    if (video.muted) {
                      video.muted = false;
                      toast.success('Audio enabled');
                    } else {
                      video.muted = true;
                      toast.success('Audio muted');
                    }
                  }}
                  className="w-full cursor-pointer"
                  style={{ maxHeight: '400px', objectFit: 'cover' }}
                >
                  <source src={link.video_url} type="video/mp4" />
                  <source src={link.video_url} type="video/webm" />
                  Your browser does not support the video tag.
                </video>
              </div>
            );
          })}

          {/* Regular Links - with ShoutOut card at position 3 if no stream cards */}
          {(() => {
            const basicLinks = links.filter(l => l.link_type === 'basic');
            const hasRumble = talentProfile?.rumble_handle && bioSettings && bioSettings.show_rumble_card !== false && rumbleData?.thumbnail;
            const hasYoutube = talentProfile?.youtube_handle && bioSettings && bioSettings.show_youtube_card !== false;
            const hasPodcast = talentProfile?.podcast_rss_url && bioSettings && bioSettings.show_podcast_card !== false;
            const hasAnyStreamCard = hasRumble || hasYoutube || hasPodcast;
            
            // ShoutOut Card Component for insertion at position 3 — respects
            // bio_settings.show_shoutout_card so creators can hide it entirely
            const ShoutOutCardInLinks = !hasAnyStreamCard && bioSettings && bioSettings.show_shoutout_card !== false ? (
              isShoutoutProfile ? <>{CreatorCommunityCard}{EmailStyleShoutOutCard}</> : (
              <a
                key="shoutout-card-inline"
                href={`/${talentProfile?.username || talentProfile?.id}/shoutout`}
                className="block"
                onClick={() => trackCardClick('shoutout')}
              >
                <div
                  className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                  style={shoutoutCardStyle}
                >
                  <div className="flex items-stretch">
                    <div className="w-20 flex-shrink-0">
                      {profileImage ? (
                        <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                          {displayName[0]}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4 flex flex-col justify-center">
                        <p className="text-sm font-bold leading-snug" style={{ color: lightTextPrimary }}>
                          Want {displayName.split(' ')[0]} to Record a Personal Video Just for You?
                        </p>

                        {randomReview ? (
                          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${lightDividerColor}` }}>
                            <div className="flex items-center gap-1 mb-0.5">
                              {[...Array(5)].map((_, i) => (
                                i < randomReview.rating ? (
                                  <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                                ) : (
                                  <StarIcon key={i} className="h-3 w-3" style={{ color: lightTextSecondary }} />
                                )
                              ))}
                            </div>
                            {randomReview.comment && (
                              <p className="text-xs line-clamp-1" style={{ color: lightTextSecondary }}>
                                "{randomReview.comment.split('\n')[0]}"
                              </p>
                            )}
                            {randomReview.users?.full_name && (
                              <p className="text-[10px] mt-0.5" style={{ color: lightTextSecondary, opacity: 0.7 }}>
                                — {randomReview.users.full_name}
                              </p>
                            )}
                          </div>
                        ) : null}
                    </div>
                  </div>
                </div>
              </a>
              )
            ) : null;
            
            // Build list of rendered elements with ShoutOut card inserted
            const allElements = basicLinks.flatMap((link, index) => {
              const elements = [];
              
              // Inline path: ShoutOutCardInLinks is the separator
              // between sponsorAds[0] (above) and sponsorAds[1] (after).
              // sponsorAds[1] only renders if ShoutOutCardInLinks is
              // present so we never end up with two ads adjacent.
              if (index === 2 && ShoutOutCardInLinks) {
                if (sponsorAds.length > 0) {
                  elements.push(<SponsorAdCard key={`sponsor-0`} ad={sponsorAds[0]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={0} talentFirstName={displayName} />);
                }
                elements.push(ShoutOutCardInLinks);
                if (sponsorAds.length > 1) {
                  elements.push(<SponsorAdCard key={`sponsor-1`} ad={sponsorAds[1]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={1} talentFirstName={displayName} />);
                }
                if (MeghanTWCCard) elements.push(MeghanTWCCard);
                if (MacBookNeoCard) elements.push(MacBookNeoCard);
                if (ShowUpNowCard) elements.push(ShowUpNowCard);
              }
              
              // Build the link element
              const isFeatured = link.is_featured;
              const featuredStyle = isFeatured ? {
                background: `linear-gradient(135deg, ${bioSettings?.button_color || '#3b82f6'}40, ${bioSettings?.gradient_end || '#1a1a2e'}60)`,
                border: `2px solid ${bioSettings?.button_color || '#3b82f6'}`,
                boxShadow: `0 0 20px ${bioSettings?.button_color || '#3b82f6'}30`,
              } : {};
              
              const linkFormat = link.link_format || 'thin';
              
              // Square format - image only, no title
              if (linkFormat === 'square') {
                elements.push(
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleLinkClick(link)}
                    className={`${getRadiusClass()} hover:scale-[1.02] active:scale-[0.98] overflow-hidden block`}
                    style={{
                      ...getButtonStyle(),
                      ...featuredStyle,
                      padding: 0,
                    }}
                  >
                    <div className="aspect-square w-full relative">
                      {link.thumbnail_url ? (
                        <img src={link.thumbnail_url} alt={link.title || ''} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white/10">
                          <ArrowTopRightOnSquareIcon className="h-8 w-8 text-white/40" />
                        </div>
                      )}
                    </div>
                  </a>
                );
                return elements;
              }
              
              // Tall format - image flush left, text on right (horizontal card)
              if (linkFormat === 'tall') {
                elements.push(
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleLinkClick(link)}
                    className={`${getRadiusClass()} hover:scale-[1.02] active:scale-[0.98] overflow-hidden`}
                    style={{
                      ...getButtonStyle(),
                      ...featuredStyle,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      padding: 0,
                    }}
                  >
                    {link.thumbnail_url && (
                      <div className="w-24 flex-shrink-0" style={{ minHeight: '96px' }}>
                        <img src={link.thumbnail_url} alt="" className="w-full h-full object-cover rounded-l-xl" />
                      </div>
                    )}
                    <div className="flex-1 flex items-center justify-between px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-left block" style={{ color: lightTextPrimary }}>{link.title}</span>
                        {link.subtitle && (
                          <span className="text-sm text-left block mt-0.5" style={{ color: lightTextSecondary }}>{link.subtitle}</span>
                        )}
                      </div>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 text-white/60 flex-shrink-0 ml-3" />
                    </div>
                  </a>
                );
                return elements;
              }
              
              // Default thin format
              elements.push(
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleLinkClick(link)}
                  className={`${getRadiusClass()} hover:scale-[1.02] active:scale-[0.98] overflow-hidden`}
                  style={{
                    ...getButtonStyle(),
                    ...featuredStyle,
                    padding: 0,
                  }}
                >
                  <div className="flex items-center">
                    {link.thumbnail_url ? (
                      <img src={link.thumbnail_url} alt="" className="w-14 h-14 object-cover flex-shrink-0" />
                    ) : link.icon_url ? (
                      <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                        <img src={link.icon_url} alt="" className="w-6 h-6" />
                      </div>
                    ) : null}
                    <div className={`flex-1 flex items-center justify-between px-4 py-4 ${!link.thumbnail_url && !link.icon_url ? 'pl-4' : ''}`}>
                      <span className="font-medium text-left" style={{ color: lightTextPrimary }}>{link.title}</span>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-shrink-0 ml-2" style={{ color: lightTextSecondary }} />
                    </div>
                  </div>
                </a>
              );
              return elements;
            });
            
            // If there were fewer than 3 basic links, ShoutOut card wasn't inserted yet - append it.
            // Same placement spec as the inline path: sponsorAds[0]
            // ABOVE ShoutOut, sponsorAds[1] AFTER (only if ShoutOut
            // card is present so we never end up with adjacent ads).
            if (basicLinks.length < 3 && ShoutOutCardInLinks) {
              if (!hasAnyStreamCard && sponsorAds.length > 0) {
                allElements.push(<SponsorAdCard key={`sponsor-0`} ad={sponsorAds[0]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={0} talentFirstName={displayName} />);
              }
              allElements.push(ShoutOutCardInLinks);
              if (!hasAnyStreamCard && sponsorAds.length > 1) {
                allElements.push(<SponsorAdCard key={`sponsor-1`} ad={sponsorAds[1]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={1} talentFirstName={displayName} />);
              }
              if (MeghanTWCCard) allElements.push(MeghanTWCCard);
            } else if (basicLinks.length === 0 && !ShoutOutCardInLinks && !hasAnyStreamCard && sponsorAds.length > 0) {
              // Edge case: no links, no stream cards, no ShoutOut card.
              // Show one sponsor ad standalone — never two, since
              // there'd be no separator card available.
              allElements.push(<SponsorAdCard key={`sponsor-solo`} ad={sponsorAds[0]} talentId={talentProfile?.id || ''} trackClick={trackCardClick} accentColor={bioSettings?.accent_color} buttonColor={bioSettings?.button_color} bgGradientStart={bioSettings?.gradient_start} bgGradientEnd={bioSettings?.gradient_end} talentUsername={talentProfile?.username} index={0} talentFirstName={displayName} />);
            }

            // MacBook Neo Giveaway card — render independently of ShoutOut card
            // so it shows even on profiles with show_shoutout_card disabled
            if (!hasAnyStreamCard && MacBookNeoCard && !allElements.some((e: any) => e?.key === 'neo-giveaway-card')) {
              allElements.push(MacBookNeoCard);
            }

            // Show Up Now card after ShoutOut card (Kaylee Campbell only)
            if (!hasAnyStreamCard && ShowUpNowCard) {
              allElements.push(ShowUpNowCard);
            }
            
            return allElements;
          })()}

          {/* Grid Links - Support different formats and layouts */}
          {links.filter(l => l.link_type === 'grid').length > 0 && (() => {
            const gridLinks = links.filter(l => l.link_type === 'grid');
            // Determine grid columns based on the grid_columns value (2=2 columns, 4=2x2)
            const gridCols = gridLinks[0]?.grid_columns || 2;
            const gridClass = gridCols >= 3 ? 'grid-cols-3' : 'grid-cols-2';
            const linkFormat = gridLinks[0]?.link_format || 'square';
            
            // Square format uses no gap for flush edges
            const gapClass = linkFormat === 'square' ? 'gap-0' : 'gap-3';
            
            return (
              <div className={`grid ${gridClass} ${gapClass} overflow-hidden rounded-xl`}>
                {gridLinks.map((link) => {
                  const format = link.link_format || 'square';
                  
                  // Square format - image only, no title, flush edges
                  if (format === 'square') {
                    return (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleLinkClick(link)}
                        className="aspect-square overflow-hidden relative group"
                      >
                        {link.thumbnail_url ? (
                          <img 
                            src={link.thumbnail_url} 
                            alt={link.title || ''} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-white/10">
                            <Squares2X2Icon className="h-8 w-8 text-white/40" />
                          </div>
                        )}
                      </a>
                    );
                  }
                  
                  // Tall format - image flush left, text on right (horizontal card)
                  if (format === 'tall') {
                    return (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleLinkClick(link)}
                        className={`${getRadiusClass()} overflow-hidden group col-span-2`}
                        style={{
                          ...getButtonStyle(),
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'stretch',
                          padding: 0,
                        }}
                      >
                        {link.thumbnail_url ? (
                          <div className="w-24 flex-shrink-0" style={{ minHeight: '96px' }}>
                            <img 
                              src={link.thumbnail_url} 
                              alt={link.title || ''} 
                              className="w-full h-full object-cover rounded-l-xl transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div className="w-24 flex-shrink-0 flex items-center justify-center bg-white/10" style={{ minHeight: '96px' }}>
                            <Squares2X2Icon className="h-8 w-8 text-white/40" />
                          </div>
                        )}
                        <div className="flex-1 flex items-center justify-between px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-left block" style={{ color: lightTextPrimary }}>{link.title}</span>
                          </div>
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-shrink-0 ml-3" style={{ color: lightTextSecondary }} />
                        </div>
                      </a>
                    );
                  }
                  
                  // Thin format - compact with image and title
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleLinkClick(link)}
                      className={`${getRadiusClass()} overflow-hidden relative group`}
                      style={getButtonStyle()}
                    >
                      {link.thumbnail_url ? (
                        <>
                          <img 
                            src={link.thumbnail_url} 
                            alt={link.title || ''} 
                            className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                            <span className="text-white font-medium text-sm">{link.title}</span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full aspect-square flex flex-col items-center justify-center p-4">
                          <Squares2X2Icon className="h-8 w-8 mb-2" style={{ color: lightTextSecondary }} />
                          <span className="font-medium text-sm text-center" style={{ color: lightTextPrimary }}>{link.title}</span>
                        </div>
                      )}
                    </a>
                  );
                })}
              </div>
            );
          })()}

          {/* Sponsor Links */}
          {links.filter(l => l.link_type === 'sponsor').map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleLinkClick(link)}
              className={`${getRadiusClass()} hover:scale-[1.02] active:scale-[0.98]`}
              style={{
                ...getButtonStyle(),
                background: 'linear-gradient(to right, rgba(234, 179, 8, 0.2), rgba(249, 115, 22, 0.2))',
                borderColor: 'rgba(234, 179, 8, 0.3)',
              }}
            >
              <div className="flex items-center justify-center">
                <span className="font-medium" style={{ color: lightTextPrimary }}>{link.title || 'Become a Sponsor'}</span>
              </div>
            </a>
          ))}

          {/* Collab/Sponsorship Service Cards */}
          {serviceOfferings.map((service) => {
            // Get platforms included in this service
            const servicePlatforms = service.platforms || ['instagram'];
            const platformLabel = servicePlatforms.length === 1 
              ? `${servicePlatforms[0].charAt(0).toUpperCase() + servicePlatforms[0].slice(1)} Collab`
              : `Collaborate with me`;
            
            // Use total_followers from the service directly
            const totalFollowers = service.total_followers || 0;
            
            // Debug logging
            console.log('Collab card debug:', {
              servicePlatforms,
              totalFollowers,
              serviceId: service.id
            });
            const formatFollowers = (count: number): string => {
              if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
              if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
              return count.toString();
            };
            
            return (
              <button
                key={service.id}
                onClick={() => {
                  trackCardClick(service.service_type === 'sponsorship' ? 'sponsorship' : 'collab');
                  if (service.service_type === 'sponsorship') {
                    setShowSponsorModal(service);
                  } else {
                    setShowCollabModal(service);
                  }
                }}
                className="w-full text-left mt-4"
              >
                <div className={`rounded-2xl overflow-hidden border transition-all duration-300 hover:scale-[1.02] ${
                  service.service_type === 'sponsorship'
                    ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 hover:border-green-500/50'
                    : 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-pink-500/30 hover:border-pink-500/50'
                }`}>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {service.service_type === 'sponsorship' ? (
                        <>
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4 text-white">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-green-400 text-xs font-medium">Opportunities</span>
                        </>
                      ) : (
                        <>
                          {/* Show icons for all platforms in this service */}
                          <div className="flex -space-x-1">
                            {servicePlatforms.slice(0, 3).map((platformId) => {
                              const platform = SOCIAL_PLATFORMS[platformId];
                              if (!platform) return null;
                              return (
                                <div key={platformId} className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0 border-2 border-[#1a1a1a]">
                                  <span className="text-white [&>svg]:w-3.5 [&>svg]:h-3.5">{platform.icon}</span>
                                </div>
                              );
                            })}
                            {servicePlatforms.length > 3 && (
                              <div className="w-7 h-7 rounded-lg bg-pink-500/50 flex items-center justify-center flex-shrink-0 border-2 border-[#1a1a1a]">
                                <span className="text-white text-xs font-bold">+{servicePlatforms.length - 3}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-pink-400 text-sm font-medium">{platformLabel}</span>
                          {/* Show total followers inline with platform label - only if set */}
                          {totalFollowers > 0 && (
                            <>
                              <span className="text-gray-400 text-sm">•</span>
                              <span className="text-pink-400 text-sm font-medium">
                                {formatFollowers(totalFollowers)} followers
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {service.title && (
                      <h3 className="font-semibold text-sm" style={{ color: lightTextPrimary }}>
                        {service.title}
                      </h3>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Next Upcoming Event - Only show one */}
          {(() => {
            // Get the next upcoming event (soonest date, or first if no dates)
            const nextEvent = [...bioEvents]
              .filter(e => e.is_active)
              .sort((a, b) => {
                if (!a.event_date) return 1;
                if (!b.event_date) return -1;
                return new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
              })[0];
            
            if (!nextEvent) return null;
            
            const startFormatted = nextEvent.event_date 
              ? new Date(nextEvent.event_date).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric' 
                })
              : null;
            const endFormatted = nextEvent.event_end_date
              ? new Date(nextEvent.event_end_date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })
              : null;
            const eventDateFormatted = startFormatted
              ? (endFormatted ? `${startFormatted} – ${endFormatted}` : startFormatted)
              : null;
            
            return (
              <a
                href={nextEvent.registration_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4"
              >
                <div 
                  className="rounded-2xl overflow-hidden border border-orange-500/30 hover:border-orange-500/50 transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: `linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(245, 158, 11, 0.2))`,
                  }}
                >
                  <div className="flex items-stretch">
                    {/* Event Image */}
                    <div className="w-24 flex-shrink-0 relative bg-black/20">
                      {nextEvent.image_url ? (
                        <img 
                          src={nextEvent.image_url} 
                          alt={nextEvent.title} 
                          className="w-full h-full object-cover"
                          style={{ minHeight: '96px' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-500" style={{ minHeight: '96px' }}>
                          <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-white">
                            <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Event Details */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm leading-tight">{nextEvent.title}</h3>
                          {(eventDateFormatted || nextEvent.event_time || nextEvent.location) && (
                            <p className="text-orange-300/80 text-xs mt-1">
                              {eventDateFormatted && <span>📅 {eventDateFormatted}</span>}
                              {nextEvent.event_time && <span> • {nextEvent.event_time}</span>}
                              {nextEvent.location && <span className="block mt-0.5">📍 {nextEvent.location}</span>}
                            </p>
                          )}
                          {nextEvent.description && (
                            <p className="text-gray-400 text-xs mt-1 line-clamp-2">{nextEvent.description}</p>
                          )}
                        </div>
                        <span 
                          className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: bioSettings?.button_color || '#f97316',
                            color: getContrastTextColor(bioSettings?.button_color || '#f97316')
                          }}
                        >
                          {nextEvent.button_text || 'Get Tickets'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            );
          })()}

          {/* No fallback sponsor render: we cap sponsorAds at 2 (after
              dedup by sponsor_id) and place them above/after the
              ShoutOut card. The ShoutOut card guarantees there's a
              real card between any two sponsor ads on the page —
              we never stack ads back-to-back, ever. */}

          {/* Affiliate Links Carousel */}
          {(() => {
            const affiliateLinks = links.filter(l => l.link_type === 'affiliate' && l.is_active);
            if (affiliateLinks.length === 0) return null;
            
            const sectionTitle = bioSettings?.affiliate_section_title || 'Back the Brands That Support Me';
            
            return (
              <div className="mt-6">
                <h3 className="text-white font-semibold text-sm mb-3 text-center">{sectionTitle}</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
                  {affiliateLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleLinkClick(link)}
                      className="flex-shrink-0 w-32 snap-center"
                    >
                      <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 shadow-lg hover:border-white/40 hover:scale-105 transition-all duration-300">
                        {/* Brand Image */}
                        <div className="aspect-square bg-white/10">
                          {link.thumbnail_url ? (
                            <img src={link.thumbnail_url} alt={link.company_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/20 to-white/5">
                              <span className="text-white/40 text-3xl font-bold">{link.company_name?.charAt(0)}</span>
                            </div>
                          )}
                          
                          {/* Discount Badge - Top Right */}
                          {(link.discount_amount || link.discount_code) && (
                            <div className="absolute top-1.5 right-1.5 bg-red-500/70 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-lg">
                              {link.discount_amount && <div className="leading-tight">{link.discount_amount}</div>}
                              {link.discount_code && <div className="text-[8px] leading-tight">{link.discount_code}</div>}
                            </div>
                          )}
                        </div>
                        
                        {/* Company Name Banner - Bottom */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm py-2 px-2">
                          <p className="text-white text-[10px] font-semibold text-center uppercase tracking-wide truncate">
                            {link.company_name}
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Social Icons */}
        {socialAccounts.length > 0 && (
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            {socialAccounts.map((social) => {
              const platform = SOCIAL_PLATFORMS[social.platform];
              if (!platform) return null;
              
              return (
                <a
                  key={social.id}
                  href={`${platform.baseUrl}${social.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCardClick(`social_${social.platform}`)}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{
                    backgroundColor: `${bioSettings?.button_color || '#3b82f6'}30`,
                    border: `1px solid ${bioSettings?.button_color || '#3b82f6'}50`,
                    color: bioSettings?.button_color || '#3b82f6',
                  }}
                  title={platform.name}
                >
                  {platform.icon}
                </a>
              );
            })}
          </div>
        )}

        {/* Footer - White text with opacity for visibility on any background */}
        <div className="mt-8 pb-20 text-center space-y-3">
          {/* Terms of Service for Newsletter */}
          {bioSettings?.show_newsletter !== false && !isFollowing && (
            <p className="text-[10px] max-w-xs mx-auto leading-relaxed" style={{ color: lightTextSecondary, opacity: 0.5 }}>
              By connecting, you agree to receive updates from {displayName.split(' ')[0]} and ShoutOut, LLC. We do not share or sell your data.
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <a 
              href="https://shoutout.us/start"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCardClick('shoutout_start_footer')}
              className="inline-flex items-center gap-1.5 opacity-30 hover:opacity-50 transition-opacity"
            >
              <img
                src={`${process.env.PUBLIC_URL}/shoutoutwhite.png`}
                alt="ShoutOut"
                className="h-4 w-auto"
                style={isLightBg ? { filter: 'brightness(0.3) sepia(1) saturate(0.5)', opacity: 0.7 } : undefined}
              />
              <span className="text-xs font-medium" style={{ color: lightTextSecondary }}>For Creators</span>
            </a>
          </div>
        </div>
      </div>

      {/* Floating Bottom Bar - Stay Connected / Support */}
      {bioSettings?.show_newsletter !== false && !(isAdsEmbed && talentProfile?.username === 'shawnfarash') && (
        <>
          <style>{`
            @keyframes stayBarTrace {
              0%   { stroke-dashoffset: 100; opacity: 0; }
              8%   { opacity: 1; }
              92%  { opacity: 1; }
              100% { stroke-dashoffset: 0; opacity: 0; }
            }
            /* Concentric waves around the user-icon when a fan completes
               sign-up — same vibe as the check-in coin waves but tinted
               to the talent's button color and sized to the 40px avatar. */
            @keyframes bioFollowWave {
              0%   { transform: scale(1);    opacity: 0.85; }
              60%  { opacity: 0.4; }
              100% { transform: scale(2.4); opacity: 0; }
            }
          `}</style>
        <div className="fixed bottom-4 left-2 right-2 z-40 flex justify-end">
          <div
            ref={stayBarRef}
            className="relative w-auto flex items-center gap-2 px-2 py-2 rounded-full border shadow-2xl backdrop-blur-xl"
            style={{
              backgroundColor: isLightBg ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              borderColor: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)',
            }}
          >
            {stayBarPulse && stayBarSize.w > 0 && (
              <svg
                aria-hidden="true"
                width={stayBarSize.w}
                height={stayBarSize.h}
                className="pointer-events-none absolute inset-0"
                style={{ overflow: 'visible' }}
              >
                <defs>
                  <linearGradient id="stayBarTraceGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={shoutoutBase} />
                    <stop offset="100%" stopColor={shoutoutAccent} />
                  </linearGradient>
                </defs>
                <rect
                  x="1"
                  y="1"
                  width={Math.max(0, stayBarSize.w - 2)}
                  height={Math.max(0, stayBarSize.h - 2)}
                  rx={stayBarSize.h / 2}
                  ry={stayBarSize.h / 2}
                  fill="none"
                  stroke="url(#stayBarTraceGrad)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  pathLength={100}
                  strokeDasharray="18 82"
                  style={{
                    filter: `drop-shadow(0 0 6px ${shoutoutBase}) drop-shadow(0 0 3px ${shoutoutAccent})`,
                    animation: 'stayBarTrace 2.2s ease-in-out forwards',
                  }}
                />
              </svg>
            )}
            {isFollowing ? (
              <>
                {showTopFans && (
                  <span
                    className="px-4 py-2 text-[11px] font-medium text-center leading-tight"
                    style={{
                      color: lightTextPrimary,
                      opacity: 0.7,
                      maxWidth: 180,
                    }}
                  >
                    Support, order, or check in daily to rank as a Top Fan.
                  </span>
                )}
                {parseFloat(String(currentUser?.credits || 0)) > 0 && (
                  <a
                    href="/account?tab=cards"
                    title="ShoutOut balance"
                    onClick={() => trackCardClick('balance_badge')}
                    className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-opacity hover:opacity-80"
                    style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
                  >
                    ${Math.floor(parseFloat(String(currentUser?.credits || 0)))}
                  </a>
                )}
                <div className="relative flex-shrink-0">
                  <a
                    href="/account"
                    onClick={() => trackCardClick('account_avatar')}
                    className="block w-10 h-10 rounded-full overflow-hidden border-2 transition-opacity hover:opacity-80"
                    style={{ borderColor: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)' }}
                  >
                    {currentUser?.avatar_url ? (
                      <img src={currentUser.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-sm font-bold"
                        style={{
                          backgroundColor: isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
                          color: lightTextPrimary,
                        }}
                      >
                        {currentUser?.full_name?.charAt(0)?.toUpperCase() || (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    )}
                  </a>
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center leading-none">
                      {unreadNotifications}
                    </span>
                  )}
                </div>
              </>
            ) : (() => {
              // Single rendering for both "logged in but not following yet" and
              // "anonymous visitor". Both get the static "Become {first}'s
              // Fan" button. Anonymous click opens AccountModal; the
              // deferred follow auto-fires via the effect after auth.
              const firstName = (displayName || 'them').split(' ')[0];
              const buttonBg = bioSettings?.button_color || '#3b82f6';
              const buttonFg = getContrastTextColor(buttonBg);
              return (
                <>
                  <button
                    type="button"
                    onClick={handleFollowSignup}
                    disabled={subscribing}
                    aria-label={`Become ${firstName}'s Fan`}
                    className="px-5 py-2.5 rounded-full font-medium text-sm text-center transition-colors disabled:opacity-50 flex items-center justify-center whitespace-nowrap"
                    style={{ backgroundColor: buttonBg, color: buttonFg }}
                  >
                    {subscribing ? '…' : `Become ${firstName}'s Fan`}
                  </button>
                  {currentUser && parseFloat(String(currentUser?.credits || 0)) > 0 && (
                    <a
                      href="/account?tab=cards"
                      title="ShoutOut balance"
                      onClick={() => trackCardClick('balance_badge')}
                      className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-opacity hover:opacity-80"
                      style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
                    >
                      ${Math.floor(parseFloat(String(currentUser?.credits || 0)))}
                    </a>
                  )}
                  {/* User icon — link for authed, button for anonymous (opens
                      AccountModal). Wrapped in a relative div so the
                      followWavesKey rings can ride on top after the fan
                      completes signup. */}
                  <div className="relative flex-shrink-0">
                    {currentUser ? (
                      <a
                        href="/account"
                        onClick={() => trackCardClick('account_avatar')}
                        className="block w-10 h-10 rounded-full overflow-hidden border-2 transition-opacity hover:opacity-80"
                        style={{ borderColor: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)' }}
                      >
                        {currentUser?.avatar_url ? (
                          <img src={currentUser.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center text-sm font-bold"
                            style={{
                              backgroundColor: isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
                              color: lightTextPrimary,
                            }}
                          >
                            {currentUser?.full_name?.charAt(0)?.toUpperCase() || (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        )}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowAccountModal(true)}
                        className="block w-10 h-10 rounded-full overflow-hidden border-2 transition-opacity hover:opacity-80 flex items-center justify-center"
                        style={{
                          borderColor: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
                          backgroundColor: isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
                        }}
                        aria-label="Log in"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" style={{ color: lightTextPrimary }}>
                          <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                    {currentUser && unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center leading-none">
                        {unreadNotifications}
                      </span>
                    )}
                    {/* Celebration waves on successful follow. Re-mounts
                        each time followWavesKey changes so the animation
                        plays from the start. */}
                    {followWavesKey > 0 && (
                      <span
                        key={followWavesKey}
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0"
                      >
                        {[0, 0.18, 0.36].map((delay, i) => (
                          <span
                            key={i}
                            className="absolute inset-0 rounded-full border-[3px]"
                            style={{
                              borderColor: buttonBg,
                              boxShadow: `0 0 18px ${buttonBg}, 0 0 36px ${buttonBg}66`,
                              animation: 'bioFollowWave 1.4s ease-out forwards',
                              animationDelay: `${delay}s`,
                            }}
                          />
                        ))}
                      </span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
        </>
      )}

      {/* Collab Service Modal */}
      {showCollabModal && (
        <CollabModal
          service={showCollabModal}
          talentProfile={talentProfile}
          socialAccounts={socialAccounts}
          accentColor={bioSettings?.button_color}
          onClose={() => setShowCollabModal(null)}
        />
      )}

      {/* Sponsor Inquiry Modal */}
      {showSponsorModal && talentProfile && (
        <SponsorInquiryModal
          service={showSponsorModal}
          talentProfile={talentProfile}
          accentColor={bioSettings?.button_color}
          onClose={() => setShowSponsorModal(null)}
        />
      )}

      {/* Podcast Modal */}
      {showPodcastModal && podcastData && bioSettings && (
        <PodcastModal
          podcast={podcastData}
          bioSettings={bioSettings}
          onClose={() => setShowPodcastModal(false)}
        />
      )}
      {/* Order Download Overlay */}
      {orderOverlay && (() => {
        const overlayTheme = getModalTheme(bioSettings?.button_color);
        return (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={overlayTheme.backdropStyle}
          >
            <div
              className="relative w-full max-w-md overflow-hidden rounded-2xl shadow-2xl"
              style={overlayTheme.shellStyle}
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-32"
                style={{
                  background: `radial-gradient(60% 60% at 0% 0%, ${overlayTheme.accentSoft} 0%, transparent 70%), radial-gradient(60% 60% at 100% 0%, ${overlayTheme.accentSoft} 0%, transparent 70%)`,
                }}
              />
              <div className="relative p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white text-lg font-extrabold tracking-tight">Your ShoutOut is Ready!</h2>
                  <button
                    onClick={() => {
                      setOrderOverlay(null);
                      setSearchParams({});
                    }}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="rounded-xl overflow-hidden bg-black mb-4" style={{ border: `1px solid ${overlayTheme.accentSoft}` }}>
                  <video
                    src={orderOverlay.videoUrl}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full"
                    style={{ maxHeight: '50vh' }}
                  />
                </div>
                <p className="text-gray-300 text-sm mb-4 text-center">
                  From <span className="font-semibold" style={{ color: overlayTheme.accent }}>{orderOverlay.talentName}</span>
                </p>
                <button
                  onClick={() => {
                    const ext = orderOverlay.videoUrl.split('.').pop()?.split('?')[0] || 'mp4';
                    const filename = `shoutout-${orderOverlay.talentName.replace(/\s+/g, '-')}-${orderOverlay.orderId.slice(0, 8)}.${ext}`;
                    downloadVideoFile(orderOverlay.videoUrl, filename);
                  }}
                  disabled={downloading}
                  className="w-full py-3 rounded-xl font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
                  style={overlayTheme.ctaStyle}
                >
                  {downloading ? 'Preparing Download...' : 'Download Video'}
                </button>
                <p className="text-gray-400 text-xs text-center mt-2">
                  On mobile? Long-press the video to save to your device.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Account / Login popup — replaces direct navigation to /account.
          When opened by the exit-intent / 8s "Become a Fan" trigger, we
          pass a valueProp so the modal's framing flips to a conversion-
          focused header + perks checklist. The auth flow itself is the
          same code path either way. */}
      <AccountModal
        open={showAccountModal && !(isEmbeddedContext && accountModalContext === 'becomeFan')}
        onClose={() => setShowAccountModal(false)}
        talentId={talentProfile?.id || null}
        talentFirstName={(displayName || '').split(' ')[0]}
        theme={{
          color: bioSettings?.button_color || '#3b82f6',
          avatarUrl: profileImage,
        }}
        valueProp={accountModalContext === 'becomeFan' ? (() => {
          const first = (displayName || 'them').split(' ')[0];
          return {
            title: `Support ${first} for free`,
            subtitle: `Support ${first}'s content by registering as a Fan for free!`,
            perks: [
              `Send messages to ${first}`,
              'Unlock exclusive discounts',
              `Support ${first} by checking in daily`,
              `Earn points to rank as ${first}'s Top Fan`,
            ],
          };
        })() : undefined}
        onAuthenticated={async (u) => {
          setCurrentUser({
            id: u.id,
            email: u.email || '',
            phone: '' as any,
            full_name: u.full_name || '',
            credits: (u.credits as any) ?? 0,
            avatar_url: u.avatar_url || null,
          } as any);
          setIsFollowing(true);
          // If the user got here via the become-fan popup, fire the same
          // wave animation around the user-icon so they get the
          // celebratory feedback the explicit "Become Fan" tap gets.
          if (accountModalContext === 'becomeFan') {
            setFollowWavesKey((k) => k + 1);
          }
          reloadLeaderboard();
          reloadFanRank();
          // NOTE: we intentionally don't fire runCheckInCore here. The modal
          // still lingers on a success state for ~1.4s before calling
          // onClose — we wait for the modal-closed effect below to run the
          // check-in so the waves/animation don't play behind the sheet.
        }}
      />
    </div>
    </>
  );
};

// Collab Service Modal Component
const CollabModal: React.FC<{
  service: ServiceOffering;
  talentProfile: TalentProfile | null;
  socialAccounts: SocialAccount[];
  accentColor?: string;
  onClose: () => void;
}> = ({ service, talentProfile, socialAccounts, accentColor, onClose }) => {
  const userName = (talentProfile as any)?.users?.full_name;
  const displayName = (
    (talentProfile?.full_name || '').trim() ||
    (talentProfile?.temp_full_name || '').trim() ||
    (userName || '').trim() ||
    'Creator'
  ).replace(/\s+/g, ' ');

  const theme = getModalTheme(accentColor);

  const servicePlatforms = service.platforms || ['instagram'];
  const filteredSocials = socialAccounts.filter(s => servicePlatforms.includes(s.platform));

  const platformLabel = servicePlatforms.length === 1
    ? `${servicePlatforms[0].charAt(0).toUpperCase() + servicePlatforms[0].slice(1)} Collab`
    : `Collaborate with me`;

  const handleStartRequest = () => {
    const orderUrl = `/collab/${talentProfile?.username || talentProfile?.id}/${service.id}`;
    window.location.href = orderUrl;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={theme.backdropStyle}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={theme.shellStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Aurora streaks */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40"
          style={{
            background: `radial-gradient(60% 60% at 0% 0%, ${theme.accentSoft} 0%, transparent 70%), radial-gradient(60% 60% at 100% 0%, ${theme.accentSoft} 0%, transparent 70%)`,
          }}
        />

        <div className="relative p-6 pb-4 border-b" style={{ borderColor: theme.accentSoft }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            ×
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex -space-x-2">
              {servicePlatforms.slice(0, 3).map((platformId) => {
                const platform = SOCIAL_PLATFORMS[platformId];
                if (!platform) return null;
                return (
                  <div
                    key={platformId}
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: theme.accent,
                      color: theme.ctaTextColor,
                      border: `2px solid rgba(0,0,0,0.45)`,
                      boxShadow: `0 6px 18px -6px ${theme.accentGlow}`,
                    }}
                  >
                    <span className="[&>svg]:w-5 [&>svg]:h-5">{platform.icon}</span>
                  </div>
                );
              })}
              {servicePlatforms.length > 3 && (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: theme.accentSoft,
                    border: `2px solid rgba(0,0,0,0.45)`,
                    color: theme.accent,
                  }}
                >
                  <span className="text-sm font-bold">+{servicePlatforms.length - 3}</span>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide" style={{ color: theme.accent }}>{platformLabel}</p>
              <h2 className="text-xl font-extrabold text-white tracking-tight">{service.title}</h2>
            </div>
          </div>

          <p className="text-gray-300 text-sm">
            Work with {displayName} on a sponsored collaboration
          </p>
        </div>

        <div className="relative p-6 space-y-6">
          <div
            className="flex items-center justify-between p-4 rounded-xl"
            style={{
              backgroundColor: theme.accentSoft,
              border: `1px solid ${theme.accentMid}`,
            }}
          >
            <span className="text-gray-200">Price</span>
            <span className="text-2xl font-extrabold text-white">${(service.pricing / 100).toFixed(0)}</span>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-3">What you get</h3>
            <div className="space-y-2">
              {[...service.benefits, `${service.video_length_seconds}+ second video`].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <ThemedCheck theme={theme} />
                  <span className="text-gray-200 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-3">Reach & Platforms</h3>
            <div className="space-y-2">
              {servicePlatforms.map((platformId) => {
                const platform = SOCIAL_PLATFORMS[platformId];
                const socialAccount = filteredSocials.find(s => s.platform === platformId);
                const formatFollowers = (count: number): string => {
                  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
                  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
                  return count.toString();
                };
                if (!platform) return null;
                return (
                  <div key={platformId} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-200 [&>svg]:w-5 [&>svg]:h-5">{platform.icon}</span>
                      <div>
                        <span className="text-white text-sm font-medium">{platform.name}</span>
                        {socialAccount && (
                          <p className="text-gray-400 text-xs">@{socialAccount.handle}</p>
                        )}
                      </div>
                    </div>
                    {socialAccount?.follower_count && socialAccount.follower_count > 0 ? (
                      <span className="font-semibold" style={{ color: theme.accent }}>{formatFollowers(socialAccount.follower_count)}</span>
                    ) : (
                      <ThemedCheck theme={theme} />
                    )}
                  </div>
                );
              })}
            </div>
            {(() => {
              const totalFollowers = filteredSocials.reduce((sum, s) => sum + (s.follower_count || 0), 0);
              const formatFollowers = (count: number): string => {
                if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
                if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
                return count.toString();
              };
              return totalFollowers > 0 ? (
                <div
                  className="mt-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: theme.accentSoft,
                    border: `1px solid ${theme.accentMid}`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-200 text-sm">Combined Reach</span>
                    <span className="font-extrabold text-lg" style={{ color: theme.accent }}>{formatFollowers(totalFollowers)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-xs mt-2 text-center">
                  Your content will be posted on {servicePlatforms.length} platform{servicePlatforms.length > 1 ? 's' : ''}
                </p>
              );
            })()}
          </div>

          {service.description && (
            <div>
              <h3 className="text-white font-semibold mb-2">About this service</h3>
              <p className="text-gray-300 text-sm">{service.description}</p>
            </div>
          )}
        </div>

        <div className="relative p-6 pt-0">
          <button
            onClick={handleStartRequest}
            className="w-full py-4 rounded-xl font-bold text-lg transition-transform active:scale-[0.98]"
            style={theme.ctaStyle}
          >
            Start Request
          </button>
          <p className="text-gray-400 text-xs text-center mt-3">
            You won't be charged until {displayName} accepts your request
          </p>
        </div>
      </div>
    </div>
  );
};

// Small reusable themed check icon for the perks/checklists across the
// CollabModal and SponsorInquiryModal — pill chip + accent-colored tick.
const ThemedCheck: React.FC<{ theme: BioModalTheme; sizePx?: number }> = ({ theme, sizePx = 20 }) => (
  <span
    className="flex-shrink-0 rounded-full flex items-center justify-center"
    style={{ ...theme.checkChipStyle, width: sizePx, height: sizePx }}
    aria-hidden="true"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill={theme.accent}
      style={{ width: Math.max(8, sizePx - 8), height: Math.max(8, sizePx - 8) }}
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.296a1 1 0 010 1.408l-7.5 7.5a1 1 0 01-1.408 0l-3.5-3.5a1 1 0 011.408-1.408L8.5 12.092l6.796-6.796a1 1 0 011.408 0z"
        clipRule="evenodd"
      />
    </svg>
  </span>
);

// Sponsor Inquiry Modal
const SponsorInquiryModal: React.FC<{
  service: ServiceOffering;
  talentProfile: TalentProfile;
  accentColor?: string;
  onClose: () => void;
}> = ({ service, talentProfile, accentColor, onClose }) => {
  const theme = getModalTheme(accentColor);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    budget: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Send sponsor inquiry to edge function
      const { error } = await supabase.functions.invoke('send-sponsor-inquiry', {
        body: {
          talent_id: talentProfile.id,
          talent_name: talentProfile.full_name || talentProfile.temp_full_name || talentProfile.users?.full_name || 'Talent',
          talent_username: talentProfile.username,
          service_title: service.title,
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          budget: formData.budget,
          message: formData.message,
        }
      });

      if (error) throw error;

      toast.success('Inquiry sent! The talent will be in touch soon.');
      onClose();
    } catch (error: any) {
      console.error('Error sending inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = (
    (talentProfile.full_name || '').trim() ||
    (talentProfile.temp_full_name || '').trim() ||
    (talentProfile.users?.full_name || '').trim() ||
    'Creator'
  ).replace(/\s+/g, ' ');

  const inputClass = "w-full rounded-xl px-4 py-3 placeholder-gray-400 focus:outline-none transition-colors";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
      style={theme.backdropStyle}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg my-8 rounded-2xl shadow-2xl overflow-hidden"
        style={theme.shellStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-32"
          style={{
            background: `radial-gradient(60% 60% at 0% 0%, ${theme.accentSoft} 0%, transparent 70%), radial-gradient(60% 60% at 100% 0%, ${theme.accentSoft} 0%, transparent 70%)`,
          }}
        />

        <div className="relative p-6 border-b" style={{ borderColor: theme.accentSoft }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-extrabold text-white tracking-tight">Sponsorship Inquiry</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="text-gray-300 text-sm">
            Send {displayName} details about your sponsorship opportunity
          </p>
        </div>

        <form onSubmit={handleSubmit} className="relative p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Company Name *</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className={inputClass}
                style={theme.inputStyle}
                {...focusBorder(theme)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Your Name *</label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className={inputClass}
                style={theme.inputStyle}
                {...focusBorder(theme)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={inputClass}
                style={theme.inputStyle}
                {...focusBorder(theme)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className={inputClass}
                style={theme.inputStyle}
                {...focusBorder(theme)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">Budget Range</label>
            <input
              type="text"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              placeholder="e.g., $5,000 - $10,000"
              className={inputClass}
              style={theme.inputStyle}
              {...focusBorder(theme)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">Message *</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your sponsorship opportunity..."
              rows={4}
              className={`${inputClass} resize-none`}
              style={theme.inputStyle}
              {...focusBorder(theme)}
              required
            />
          </div>

          {service.benefits && service.benefits.length > 0 && (
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: theme.accentSoft, border: `1px solid ${theme.accentMid}` }}
            >
              <h3 className="text-white font-semibold mb-2 text-sm">What you can expect:</h3>
              <ul className="space-y-1.5">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-200 text-sm">
                    <ThemedCheck theme={theme} sizePx={16} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-xl font-medium hover:bg-white/10 transition-colors"
              style={theme.ghostStyle}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 rounded-xl font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
              style={theme.ctaStyle}
            >
              {submitting ? 'Sending...' : 'Send Inquiry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Podcast Modal Component
const PodcastModal: React.FC<{
  podcast: PodcastEpisodeData;
  bioSettings: BioSettings;
  onClose: () => void;
}> = ({ podcast, bioSettings, onClose }) => {
  const theme = getModalTheme(bioSettings.button_color);

  // Listen platforms - only show platforms with valid URLs
  console.log('Podcast listen links:', podcast.listenLinks);
  
  const listenPlatforms = [
    { name: 'Spotify', url: podcast.listenLinks?.spotify, icon: 'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z' },
    { name: 'Apple Podcasts', url: podcast.listenLinks?.apple, icon: 'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' },
    { name: 'YouTube Music', url: podcast.listenLinks?.youtube, icon: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
    { name: 'Google Podcasts', url: podcast.listenLinks?.google, icon: 'M1.26 8.265v7.47h1.96v-7.47zm3.915 0v7.47h1.96v-7.47zm3.916-3.915v15.3h1.96v-15.3zm3.915 1.957v11.386h1.96V6.307zm3.916 1.958v7.47h1.96v-7.47zm3.915 0v7.47H24v-7.47z' },
    { name: 'RSS Feed', url: podcast.feedUrl, icon: 'M4.262 4.262c5.854-5.854 15.36-5.854 21.213 0 5.854 5.854 5.854 15.36 0 21.213-5.854 5.854-15.36 5.854-21.213 0-5.854-5.854-5.854-15.36 0-21.213zM4 18c0 1.105.895 2 2 2s2-.895 2-2-.895-2-2-2-2 .895-2 2zm6.5 5a1 1 0 01-.998-1.066c.123-1.952 1.094-3.72 2.662-4.85 1.567-1.13 3.56-1.475 5.46-.947a1 1 0 11-.53 1.929c-1.415-.394-2.898-.136-4.065.707-1.167.843-1.912 2.155-2.04 3.6a1 1 0 01-1 .627H10.5zM18 19a1 1 0 01-.997-1.077c.107-2.886 1.446-5.508 3.671-7.188 2.226-1.68 5.034-2.222 7.7-1.485a1 1 0 11-.466 1.945c-2.016-.557-4.122-.147-5.775 1.123-1.653 1.27-2.679 3.239-2.76 5.405A1 1 0 0118 19z' },
  ].filter(p => p.url); // Only show platforms with URLs

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={theme.backdropStyle}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={theme.shellStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-32"
          style={{
            background: `radial-gradient(60% 60% at 0% 0%, ${theme.accentSoft} 0%, transparent 70%), radial-gradient(60% 60% at 100% 0%, ${theme.accentSoft} 0%, transparent 70%)`,
          }}
        />

        <div className="relative">
          {podcast.thumbnail && (
            <div className="w-full h-48 overflow-hidden flex items-start justify-center bg-black/20 rounded-t-2xl">
              <img src={podcast.thumbnail} alt={podcast.title} className="h-full w-auto object-contain" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/80 bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="relative p-6 space-y-4">
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" style={{ color: theme.accent }}>
              <path d="M12 1c-6.1 0-11 4.9-11 11s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm0 20c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"/>
              <circle cx="12" cy="12" r="2"/>
            </svg>
            <p className="text-sm font-medium uppercase tracking-wide" style={{ color: theme.accent }}>{podcast.podcastName}</p>
          </div>

          <h2 className="text-2xl font-extrabold text-white tracking-tight">{podcast.title}</h2>

          {podcast.description && (
            <p className="text-gray-200 text-sm leading-relaxed">{podcast.description}</p>
          )}

          {podcast.duration && (
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{podcast.duration}</span>
            </div>
          )}

          <div className="space-y-3 pt-2">
            <h3 className="text-white font-semibold text-sm">Listen on:</h3>
            <div className="space-y-2">
              {listenPlatforms.map((platform) => {
                if (!platform.url) return null;

                return (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: theme.accentSoft,
                      border: `1px solid ${theme.accentMid}`,
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0" style={{ color: theme.accent }}>
                      <path d={platform.icon} />
                    </svg>
                    <span className="text-white font-medium text-sm">{platform.name}</span>
                    <svg className="w-4 h-4 text-white/60 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sponsor Ad Card - displayed on bio pages for pre-paid sponsor campaigns
// Derive a bright, fun gradient that *matches the creator's vibe* but is
// noticeably brighter/more vivid than the bio chrome so the card reads as a
// distinct ad slot, not just another bio element.
//
// Hue picking is layered because most creators leave the platform-default
// accent/button (#3b82f6 blue). To avoid every bio rendering an identical
// blue gradient we walk a priority list of *style signals*:
//
//   1. `gradient_start` — the creator's actual page backdrop top stop.
//      This is the strongest "look & feel" signal and the field creators
//      customize most. We skip near-black values (the default
//      `#0a0a0a`/`#1a1a2e` pair) so dark-themed bios fall through.
//   2. `gradient_end` — same, just the bottom stop.
//   3. `button_color` — primary CTA color; e.g. Isabella's pink (#fe90cb).
//      Skipped when it's still the platform default `#3b82f6`.
//   4. `accent_color` — UI accent; same skip rule.
//   5. `seedKey` hash — deterministic FNV-1a over the talent's username so
//      two creators who both left every style field at the default still
//      end up with *different* gradients (variety > monotony).
//
// Saturation is forced into the 80–98% band and lightness is clamped to
// 50–62% so we never produce a muddy or washed-out result; ±30° hue offset
// between the two stops keeps the gradient feeling like one palette rather
// than two competing colors.
type HouseAdGradientInputs = {
  accentColor?:   string;
  buttonColor?:   string;
  gradientStart?: string;
  gradientEnd?:   string;
  // Stable identifier used to derive a per-creator hue when none of the
  // explicit color signals are usable. Talent username / id both work.
  seedKey?:       string;
};

function houseAdGradient(inputs: HouseAdGradientInputs): { start: string; end: string } {
  const hexToHsl = (hex: string): { h: number; s: number; l: number } | null => {
    const h = (hex || '').replace('#', '').trim();
    let r = 0, g = 0, b = 0;
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16);
      g = parseInt(h[1] + h[1], 16);
      b = parseInt(h[2] + h[2], 16);
    } else if (h.length === 6) {
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    } else {
      return null;
    }
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hh = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
        case g: hh = (b - r) / d + 2; break;
        case b: hh = (r - g) / d + 4; break;
      }
      hh *= 60;
    }
    return { h: hh, s: s * 100, l: l * 100 };
  };

  // Defaults that mean "creator hasn't customized this slot" — we don't
  // want to seed off of them because every uncustomized bio would map to
  // the same gradient.
  const PLATFORM_DEFAULT_ACCENT = '#3b82f6';
  const norm = (v?: string) => (v || '').trim().toLowerCase();
  const isPlatformDefault = (v?: string) => norm(v) === PLATFORM_DEFAULT_ACCENT;

  // A color counts as "usable" when it parses, isn't near-black (l < 18%),
  // and has at least some chroma (s > 12%) — otherwise the ±30° hue offset
  // produces two indistinguishable greys.
  const usable = (v?: string): { h: number; s: number; l: number } | null => {
    if (!v) return null;
    if (isPlatformDefault(v)) return null;
    const hsl = hexToHsl(v);
    if (!hsl) return null;
    if (hsl.l < 18) return null;
    if (hsl.s < 12) return null;
    return hsl;
  };

  let hsl =
    usable(inputs.gradientStart) ||
    usable(inputs.gradientEnd) ||
    usable(inputs.buttonColor) ||
    usable(inputs.accentColor);

  if (!hsl) {
    // No usable creator color. Derive a stable hue from the seedKey so two
    // default-styled bios still render different gradients (e.g. Nick and
    // Shawn both have all-default colors but should look different to
    // viewers who hit both pages).
    const seed = inputs.seedKey || inputs.accentColor || 'shoutout';
    let h = 0x811c9dc5; // FNV-1a 32-bit offset basis
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      // Math.imul keeps it 32-bit; mixing constant from FNV-1a.
      h = Math.imul(h, 0x01000193);
    }
    const hue = ((h >>> 0) % 360);
    hsl = { h: hue, s: 85, l: 55 };
  }

  const sat    = Math.max(80, Math.min(98, hsl.s));
  const lStart = Math.max(50, Math.min(62, hsl.l + 8));
  const lEnd   = Math.max(46, Math.min(58, hsl.l + 2));
  const hStart = ((hsl.h + 30) % 360 + 360) % 360;
  const hEnd   = ((hsl.h - 30) % 360 + 360) % 360;
  return {
    start: `hsl(${hStart.toFixed(0)}, ${sat.toFixed(0)}%, ${lStart.toFixed(0)}%)`,
    end:   `hsl(${hEnd.toFixed(0)}, ${sat.toFixed(0)}%, ${lEnd.toFixed(0)}%)`,
  };
}

const SponsorAdCard: React.FC<{
  ad: {
    assignment_id: string;
    campaign_id:   string;
    sponsor_id:    string;
    ad_id?:        string;
    title:         string;
    subtitle?:     string;
    logo_url?:     string;
    link_url:      string;
    discount_text?:    string;
    // Campaign-level animated brand asset. When kind='video' we
    // render an autoplay/muted/loop <video> in the thumbnail slot;
    // when kind='image' (gif/webp) we render it as <img>. Falls
    // back to the static logo_url on load error.
    creator_media_url?:  string;
    creator_media_kind?: 'video' | 'image';
    // Creative pool variant that was picked for this impression.
    // Sent to track-sponsor-click on click so per-creative CTR can be
    // computed independently of the copy CTR (Meta-Advantage style).
    creative_id?:        string;
    // House ad bits — only set for the ShoutOut "Sponsors" house campaign.
    // is_house_ad flips the render to the gradient layout below; the
    // featured_avatars list is the 3 overlapping circle thumbnails used in
    // place of a traditional product image. If the campaign admin uploads
    // a real logo onto the ad (ad.logo_url), that image replaces the
    // circles so the card always honours an explicit override.
    is_house_ad?:      boolean;
    featured_avatars?: string[];
  };
  talentId: string;
  trackClick: (type: string) => void;
  accentColor?: string;
  // Bio's button color — frequently customized by creators (where accent_color
  // is left as the platform default for many). Used by the house ad gradient
  // so the card visually matches the creator's brand. Falls back to
  // accentColor when not set.
  buttonColor?: string;
  // Bio's page-background gradient stops. These are the strongest "style"
  // signal for the house ad gradient since creators customize the page
  // backdrop far more often than accent_color/button_color.
  bgGradientStart?: string;
  bgGradientEnd?:   string;
  // Stable per-creator key used as a final fallback to derive a unique
  // gradient hue when every other color signal is the platform default.
  // Talent username is the natural choice.
  talentUsername?:  string;
  // Index in the sponsorAds array. Drives the alternating left/right
  // placement of the "{first}'s pick" pill so a stack of three ads
  // doesn't look like a stack of identical chrome.
  index?: number;
  // Talent's first name for the "{first}'s pick" personalisation pill.
  // Falls back gracefully when undefined — no pill is rendered if we
  // don't have a usable name to put in it.
  talentFirstName?: string;
}> = ({ ad, talentId, trackClick, accentColor, buttonColor, bgGradientStart, bgGradientEnd, talentUsername, index = 0, talentFirstName }) => {
  // Falls back to the static logo_url when the campaign's
  // animated MP4 fails to load (CDN blip, codec issue). Without
  // this the slot would be an empty container.
  const [mediaFailed, setMediaFailed] = React.useState(false);

  // Append ?utm_source=shoutout (or &utm_source=shoutout) to the destination
  // URL so advertisers automatically get source attribution in their analytics
  // even if they forget to add UTM params themselves. Existing utm_source params
  // are preserved. Uses the URL API so ? vs & is handled correctly.
  const buildAdUrl = (raw: string | null | undefined): string => {
    if (!raw) return '#';
    try {
      const u = new URL(raw);
      if (!u.searchParams.has('utm_source')) {
        u.searchParams.set('utm_source', 'shoutout');
      }
      return u.toString();
    } catch {
      const sep = raw.includes('?') ? '&' : '?';
      return `${raw}${sep}utm_source=shoutout`;
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick('sponsor_ad');
    // Fire-and-forget to the edge function so it can hash the real client IP
    // server-side (cf-connecting-ip / x-forwarded-for) with a salt that isn't
    // exposed to the browser. The old path hashed a UA prefix in JS, which
    // meant every Chrome user shared the same "viewer_ip_hash" — useless for
    // dedupe and fraud detection.
    try {
      supabase.functions.invoke('track-sponsor-click', {
        body: {
          campaign_id: ad.campaign_id,
          talent_id:   talentId,
          ad_id:       ad.ad_id || null,
          creative_id: ad.creative_id || null,
          referrer:    document.referrer.slice(0, 500),
        },
      }).catch(() => {});
    } catch { /* non-blocking */ }
    window.open(buildAdUrl(ad.link_url), '_blank', 'noopener,noreferrer');
  };

  // Build the themed "{first}'s pick" pill style from the talent's accent.
  // Reuse the same parseColor helper the modal theme uses so a yellow
  // accent gets dark text and a navy accent gets white text — keeps the
  // pill legible regardless of who owns the bio page.
  const pickPill = (() => {
    const first = (talentFirstName || '').split(' ')[0].trim();
    if (!first || !accentColor) return null;
    const rgb = (() => {
      const h = accentColor.replace('#', '').trim();
      if (h.length === 3) return { r: parseInt(h[0] + h[0], 16), g: parseInt(h[1] + h[1], 16), b: parseInt(h[2] + h[2], 16) };
      if (h.length === 6) return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
      return { r: 59, g: 130, b: 246 };
    })();
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    const textColor = luminance > 0.6 ? '#0b1020' : '#ffffff';
    const glow = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.55)`;
    const onLeft = index % 2 === 0;
    return (
      <div
        aria-label={`${first}'s pick`}
        className={`absolute -top-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap shadow-md ${onLeft ? 'left-3' : 'right-3'}`}
        style={{
          backgroundColor: accentColor,
          color: textColor,
          boxShadow: `0 4px 12px -2px ${glow}`,
        }}
      >
        {first}'s pick
      </div>
    );
  })();

  // House ad render: gradient background tied to the creator's accent, 3
  // overlapping circle thumbnails of featured creators (or an admin-uploaded
  // image override) on the left, ad title/subtitle on the right. Visually
  // distinct from a paid sponsor card so viewers read it as ShoutOut house
  // promotion, not a third-party brand placement.
  if (ad.is_house_ad) {
    // Seed the gradient off the strongest available "creator style" signal.
    // The helper walks the priority list (gradient_start → gradient_end →
    // button → accent → username hash), skipping platform defaults and
    // near-black values so dark-themed bios still get a vivid, per-creator
    // gradient rather than a generic blue.
    const gradient = houseAdGradient({
      accentColor,
      buttonColor,
      gradientStart: bgGradientStart,
      gradientEnd:   bgGradientEnd,
      seedKey:       talentUsername || talentId,
    });
    const featured = (ad.featured_avatars || []).filter(Boolean).slice(0, 3);
    // When an admin uploads a logo onto a house ad, that image takes the
    // visual slot — overrides the 3-circle default.
    const hasOverrideMedia = !!(ad.creator_media_url || ad.logo_url);

    // Circle dimensions for the Venn triangle.
    // Top row: circles 0 and 1, slightly overlapping.
    // Bottom: circle 2 centered between them.
    // Container is 52×52px, circles are 30px.
    const CIRCLE = 30;
    const HALF   = CIRCLE / 2;
    const VENN_W = 52;
    const VENN_H = 52;

    const vennPositions = [
      { top: 0,              left: 0                    }, // top-left
      { top: 0,              left: VENN_W - CIRCLE      }, // top-right
      { top: VENN_H - CIRCLE, left: (VENN_W - CIRCLE) / 2 }, // bottom-center
    ];

    return (
      <div className="relative mt-3">
        <a
          href={buildAdUrl(ad.link_url)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          aria-label={ad.title}
          className="block rounded-2xl overflow-hidden hover:scale-[1.02] transition-all duration-300 shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${gradient.start} 0%, ${gradient.end} 100%)`,
          }}
        >
          <div className="flex items-center">
            {/* Left slot: triangular Venn of 3 creator avatars (or override media) */}
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: hasOverrideMedia ? 80 : VENN_W + 24,
                paddingLeft: 12,
                paddingRight: 4,
              }}
            >
              {hasOverrideMedia ? (
                ad.creator_media_url && ad.creator_media_kind === 'video' && !mediaFailed ? (
                  <video
                    src={ad.creator_media_url}
                    autoPlay muted loop playsInline preload="metadata"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    onError={() => setMediaFailed(true)}
                  />
                ) : ad.creator_media_url && !mediaFailed ? (
                  <img
                    src={ad.creator_media_url}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    loading="lazy"
                    onError={() => setMediaFailed(true)}
                  />
                ) : ad.logo_url ? (
                  <img
                    src={ad.logo_url}
                    alt=""
                    className="w-14 h-14 rounded-full object-contain border-2 border-white shadow-md p-1"
                    style={{ background: 'rgba(255,255,255,0.15)' }}
                  />
                ) : null
              ) : (
                // Triangular Venn: 3 circles absolutely positioned at
                // top-left, top-right, and bottom-center so they form
                // a tight equilateral triangle that overlaps like a Venn.
                <div style={{ position: 'relative', width: VENN_W, height: VENN_H, flexShrink: 0 }}>
                  {featured.length > 0 ? (
                    featured.map((url, i) => (
                      <div
                        key={`${url}-${i}`}
                        className="rounded-full overflow-hidden border-2 border-white shadow-md"
                        style={{
                          position: 'absolute',
                          width: CIRCLE,
                          height: CIRCLE,
                          top:  vennPositions[i]?.top  ?? 0,
                          left: vennPositions[i]?.left ?? 0,
                          zIndex: 3 - i,
                        }}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ))
                  ) : (
                    // Fallback: single placeholder circle
                    [0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="rounded-full border-2 border-white shadow-md"
                        style={{
                          position: 'absolute',
                          width: CIRCLE, height: CIRCLE,
                          top:  vennPositions[i]?.top  ?? 0,
                          left: vennPositions[i]?.left ?? 0,
                          background: 'rgba(255,255,255,0.25)',
                          zIndex: 3 - i,
                        }}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
            {/* Right: ad copy */}
            <div className="flex-1 py-4 pr-4 flex flex-col justify-center">
              <p className="text-sm font-bold text-white leading-snug" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                {ad.title}
              </p>
              {ad.subtitle && (
                <p className="text-xs text-white/90 mt-1 leading-snug" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                  {ad.subtitle}
                </p>
              )}
            </div>
          </div>
        </a>
      </div>
    );
  }

  return (
    <div className="relative mt-3">
      {pickPill}
      {ad.discount_text && (
        <div className="absolute -top-2 -right-2 z-10 px-2.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full shadow-lg">
          {ad.discount_text}
        </div>
      )}
      <a
        href={buildAdUrl(ad.link_url)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="block rounded-2xl overflow-hidden border border-white/20 hover:scale-[1.02] transition-all duration-300"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        <div className="flex items-stretch">
          <div className="w-20 flex-shrink-0 flex items-center justify-center overflow-hidden bg-black/20">
            {/* Render order:
                 1. creator_media_url (kind='video') — campaign-level
                    looping MP4 of the brand logo / product image.
                    autoplay/muted/playsinline so iOS Safari plays
                    without user gesture.
                 2. creator_media_url (kind='image' / gif) — same
                    asset rendered as <img>.
                 3. logo_url — sponsor-supplied static logo.
                 4. Generic gradient placeholder. */}
            {ad.creator_media_url && ad.creator_media_kind === 'video' && !mediaFailed ? (
              <video
                src={ad.creator_media_url}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                style={{ minHeight: '72px' }}
                onError={() => setMediaFailed(true)}
              />
            ) : ad.creator_media_url && !mediaFailed ? (
              <img
                src={ad.creator_media_url}
                alt=""
                className="w-full h-full object-cover"
                style={{ minHeight: '72px' }}
                loading="lazy"
                onError={() => setMediaFailed(true)}
              />
            ) : ad.logo_url ? (
              <img src={ad.logo_url} alt="" className="w-full h-full object-contain" style={{ minHeight: '72px' }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-500/20 to-orange-500/20" style={{ minHeight: '72px' }}>
                <ArrowTopRightOnSquareIcon className="w-6 h-6 text-red-400" />
              </div>
            )}
          </div>
          <div className="flex-1 p-4 flex flex-col justify-center">
            <p className="text-sm font-semibold text-white">{ad.title}</p>
            {ad.subtitle && <p className="text-xs text-gray-400 mt-0.5">{ad.subtitle}</p>}
          </div>
        </div>
      </a>
    </div>
  );
};

export default BioPage;
